// LiqMapPage — orchestrator. Handles auth + trial gating, fetches clusters
// and candles, opens the WS for live price, and composes the visual layout.
//
// Behaviour:
//   - unauthenticated     → opens the login modal
//   - first-time auth     → POST /trial/activate then load
//   - trial active or PRO → full map
//   - trial expired no-PRO → blurred map + TrialOverlay (no close button)
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, ArrowLeft, ArrowDown, ArrowUp, X as XIcon, Sparkles, ClipboardList, Target, TrendingUp, TrendingDown, Share2, Check, Info } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../i18n/LanguageContext";
import { useHardcodedI18n } from "../../i18n/hardcoded";
import { API, API_BASE } from "../../lib/api";
import { tLm } from "../../i18n/liqmap";
import { ASSETS } from "../../assets";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";

import CoinSelector from "./CoinSelector";
import TimeframeSelector from "./TimeframeSelector";
import TrialBadge from "./TrialBadge";
import TrialOverlay from "./TrialOverlay";
import StatsBar from "./StatsBar";
import HeatmapCanvas from "./HeatmapCanvas";
import StochasticSubpanel from "./StochasticSubpanel";
import CompositeSignalBadge from "./CompositeSignalBadge";
import { computeStochastic, detectStochasticCrosses } from "./stochasticMath";
import { detectAllEMACrosses } from "./emaMath";
import { loadDrawings, saveDrawings, TOOLS as DRAW_TOOLS, serializeShare, parseShare } from "./drawingEngine";
import { captureChartAsBlob, tryNativeShare } from "../../lib/captureChart";
import rebelWatermarkUrl from "../../assets/branding/rebel_watermark.png";
import { PublishAnalysisModal } from "../PublishAnalysisModal";
import HeatmapRangeBrush from "./HeatmapRangeBrush";
import ParticlesBackground from "./ParticlesBackground";
import LiqScoreWidget from "./LiqScoreWidget";
import LiqScorePill from "./LiqScorePill";
import CascadeScoreHistoryChart from "./CascadeScoreHistoryChart";
import CascadeRecentDrawer from "./CascadeRecentDrawer";
import StructureDrawer from "./StructureDrawer";
import CascadeTimerHistoryDrawer from "./CascadeTimerHistoryDrawer";
import SrTopLevelsDrawer from "./SrTopLevelsDrawer";
import { useClusterData } from "../../hooks/useClusterData";
import {
  useCascadePredictor,
  cascadeTipo,
  CASCADE_SHOW_THRESHOLD,
} from "../../hooks/useCascadePredictor";

const WS_URL = (() => {
  try {
    const u = new URL(API_BASE);
    u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
    return u.toString().replace(/\/$/, "");
  } catch {
    return "";
  }
})();

export default function LiqMapPage() {
  const { user, token, loading: authLoading, openLogin, openRegister, isPro } = useAuth();
  const { lang } = useLanguage();
  const t = tLm(lang);
  const hc = useHardcodedI18n(lang);

  const [coin, setCoin] = useState("BTC");
  const [timeframe, setTimeframe] = useState("1h");
  const [category, setCategory] = useState("all"); // "all" | "cex" | "dex"
  const [status, setStatus] = useState(null); // trial status payload
  const [snapshot, setSnapshot] = useState(null);
  const [candles, setCandles] = useState([]);
  // Set when the backend served candles from cache because OKX+Gate.io
  // were both down. Object: { age_s }. Cleared on fresh response.
  // Surfaced as a small "datos de hace Xs" badge near the chart so the
  // user knows the data is stale (degraded mode, not silent stalling).
  const [candlesStale, setCandlesStale] = useState(null);
  // Set when GET /api/liqmap/{coin} fell back to a snapshot older than
  // the latest tick because the latest had error / empty clusters
  // (insufficient exchanges OK, validate_cluster_max rejection, etc.).
  // Independent from candlesStale — different upstream, different outage.
  const [snapshotStale, setSnapshotStale] = useState(null);
  // Time-range brush selection over `candles`. null = full range (initial
  // state and after reset). Cleared whenever coin/timeframe changes, see
  // the useEffect just below.
  const [brushRange, setBrushRange] = useState(null);
  // Whenever the data context changes (coin or timeframe), the brush
  // selection no longer maps to meaningful candle indices — reset it so
  // the heatmap always opens on the full range like before this feature.
  useEffect(() => { setBrushRange(null); }, [coin, timeframe]);
  // Sliced candles for the heatmap. When brushRange is null we keep the
  // exact reference so React.memo / useMemo identities upstream don't
  // churn. Slice only when needed.
  const displayedCandles = useMemo(() => {
    if (!brushRange || !candles.length) return candles;
    const [s, e] = brushRange;
    return candles.slice(s, e + 1);
  }, [candles, brushRange]);
  const [livePrice, setLivePrice] = useState(null);
  const [highlight, setHighlight] = useState(null);  // {price, ts} — triggers a pulse on the canvas
  const [legendOpen, setLegendOpen] = useState(false);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const legendRef = useRef(null);
  const legendBtnRef = useRef(null);
  // Wrapper that contains the layer-toggle toolbar + the heatmap canvas.
  // Used as the fullscreen target so toggles stay visible when fullscreen
  // is active (the HeatmapCanvas internal rootRef alone would hide the
  // toolbar since the toolbar lives outside that ref). See HeatmapCanvas
  // `fullscreenTargetRef` prop for the override hook.
  const chartContainerRef = useRef(null);

  // ─── Cascade Predictor (extracted to /app/frontend/src/hooks/useCascadePredictor.js
  // in 2026-02 FASE 4 refactor — see that file for FIX A/FIX B and the
  // legacy bug history of this block.)
  // Note: the hook owns score/data/loading/refs/effects/persistence; the
  // wire-up here is just the consumer side.
  // ─── FEATURE 2 (2026-02): countdown de cascada en tiempo real ──────
  const [cascadeTimer, setCascadeTimer] = useState(null);       // último JSON del endpoint
  const [cascadeTimerLocal, setCascadeTimerLocal] = useState(null); // minutos restantes (ticker local 1s)
  // ─── Institutional cluster validation toast (2026-02) ───
  // Tracks which (coin · timeframe · cluster_price) combos we've already
  // celebrated, so a confidence ≥0.5 reading only fires the share-toast
  // once per session per validated cluster.
  const toastedValidations = useRef(new Set());

  // ─── Personal Liquidation Simulator state (added 2026-06-15) ───
  const [simOpen, setSimOpen] = useState(false);
  const [simEntryInput, setSimEntryInput] = useState("");
  const [simLeverage, setSimLeverage] = useState(10);
  const [simDirection, setSimDirection] = useState("LONG"); // LONG | SHORT
  const [simSizeInput, setSimSizeInput] = useState("");
  const [simResult, setSimResult] = useState(null);
  const [shareCopied, setShareCopied] = useState(false);
  const sharedParamsRef = useRef(null); // params parsed from URL, applied once

  // ─── Whale Liquidations Layer (added 2026-02-16) — Layer 5 ──
  const [showWhales, setShowWhales] = useState(false);
  const [whaleData, setWhaleData] = useState(null);
  const [whaleLoading, setWhaleLoading] = useState(false);

  // ─── Support / Resistance IA (Layer 6 — added 2026-02-26) ──
  const [showSR, setShowSR] = useState(false);
  // "Rango de Negociación" toggle — simple high/low of the displayed
  // candles. Independent from S/R IA: can be active simultaneously.
  const [showRange, setShowRange] = useState(false);
  // "Tendencia" toggle — diagonal trendline through recent swing pivots.
  // Independent from S/R IA and Rango. Pure frontend math (trendlineMath).
  const [showTrendline, setShowTrendline] = useState(false);
  // "Patrones" toggle — algorithmic Double Top / Double Bottom detector.
  // 100% deterministic geometry on top of the same swing-pivot detector
  // used by Trendline (chartPatterns.js → reuses trendlineMath.detectSwings).
  const [showPatterns, setShowPatterns] = useState(false);
  // "Velas" toggle — candlestick pattern detector (Hammer/Doji/Engulfing).
  // Independent layer from chartist patterns; lives in its own toggle so
  // both can coexist (or be hidden) without visual collision.
  const [showCandles, setShowCandles] = useState(false);
  // EMA overlay toggle — shows the 4 EMA lines (9/21/50/200) and the 3
  // cross detections (golden/death, 21×50, 9×21). Single toggle, no
  // per-period granularity by design (UI simplicity).
  const [showEMA, setShowEMA] = useState(false);
  // Stochastic oscillator (Slow, K=14/D=3/smooth=3) — toolbar toggle that
  // reveals a subpanel below the main chart and marks cross signals on
  // the heatmap when they occur inside overbought/oversold zones.
  const [showStochastic, setShowStochastic] = useState(false);
  // Stochastic %K × %D crosses inside extreme zones (bull < 20 · bear > 80).
  // Recomputed only when the toggle is on and candles change — cheap
  // (~O(N) with tiny constants) but still gated so we never allocate
  // when the feature is idle.
  // Stochastic crosses — computed unconditionally (cheap O(N)) so the
  // Composite Signal badge can always factor them in even when the
  // Stochastic toolbar toggle is off. Visual rendering (chart dots +
  // subpanel) still respects the toggle.
  const stochasticCrosses = useMemo(() => {
    if (!Array.isArray(candles) || candles.length < 17) return [];
    const { k, d } = computeStochastic(candles);
    return detectStochasticCrosses(candles, k, d);
  }, [candles]);

  // ── Composite Signal — aggregates EMA/Stoch/Cluster/Wyckoff/Cascade
  // signals into a single verdict pill in the header. EMA crosses are
  // recomputed here (rather than reusing HeatmapCanvas' internal state)
  // so the badge stays independent of chart internals; cheap O(N) pass
  // with the per-coin/tf pair filter already built into emaMath.
  const emaCrossesForComposite = useMemo(() => {
    if (!Array.isArray(candles) || candles.length < 210) return [];
    try {
      const all = detectAllEMACrosses(candles, coin, timeframe);
      // Flatten into a single chronological array with `pair` label
      // ("9x21" | "21x50" | "50x200") for the composite scorer.
      const out = [];
      for (const [pair, list] of Object.entries(all || {})) {
        if (!Array.isArray(list)) continue;
        for (const c of list) out.push({ ...c, pair });
      }
      out.sort((a, b) => (a.idx || 0) - (b.idx || 0));
      return out;
    } catch { return []; }
  }, [candles, coin, timeframe]);
  // Wyckoff Structure drawer (V1) — opens via badge in sub-header. Reads
  // /api/liqmap/wyckoff/{coin} for range + spring/utad + retrospective sc/ar.
  const [structureOpen, setStructureOpen] = useState(false);
  // Lightweight badge state — fetched alongside snapshot to label the trigger
  // without opening the drawer. Holds {is_ranging, tf, has_recent_event}.
  const [structureBadge, setStructureBadge] = useState(null);
  // ── Drawing engine state (Fase B1) ─────────────────────────────────
  // `showDrawingTool` reveals the floating drawing toolbar AND enables
  // the dedicated `drawRef` canvas inside HeatmapCanvas. `activeTool`
  // controls which primitive the next click creates; `magnetOn` is a
  // modal flag that snaps anchors to candle vertices (OHLC). `drawings`
  // is the list of saved primitives, anchors stored in chart space
  // {idx, price} so pan/zoom doesn't drift them.
  const [showDrawingTool, setShowDrawingTool] = useState(false);
  const [activeTool, setActiveTool] = useState("cursor");
  // Single source of truth for which group-flyout is currently open in the
  // drawing toolbar. Holds the group id (e.g. "lineas", "fibos") or `null`.
  // Using one state instead of N booleans guarantees mutual exclusivity:
  // opening group B while A is open automatically closes A.
  const [openGroup, setOpenGroup] = useState(null);
  const [magnetOn, setMagnetOn] = useState(false);
  const [drawings, setDrawings] = useState(() => loadDrawings(coin, timeframe));
  // Fase B3a — global lock/hide + persistent drawing mode. These do NOT
  // persist to localStorage (they are session-only UX toggles that
  // affect rendering and mutation of the per-(coin,tf) drawings list).
  const [allDrawingsLocked, setAllDrawingsLocked] = useState(false);
  const [allDrawingsHidden, setAllDrawingsHidden] = useState(false);
  const [persistentMode, setPersistentMode] = useState(false);

  // ── B7c — Undo/Redo for drawings (session-only, max 20 states) ─────
  // Snapshots live in refs (not state) so the rapid mid-drag updates
  // from B7a don't trigger render storms. The setter `commitDrawings`
  // wraps `setDrawings` and coalesces consecutive snapshots that arrive
  // within COALESCE_MS so a single drag/gesture maps to ONE undo entry.
  //
  // No localStorage: undo only spans the current session. Matches the
  // semantics of every desktop editor (history dies at app exit).
  //
  // `historyTick` is a tiny state value bumped after each push/undo/redo
  // so reactive consumers (the toolbar undo/redo buttons, if/when added)
  // can re-render their disabled state. The stacks themselves are NOT
  // state — they'd cause infinite re-render loops since drag updates
  // would race with their own undo writes.
  const UNDO_LIMIT = 20;
  const COALESCE_MS = 200;
  const undoStackRef = useRef([]);   // [drawings[], …] oldest→newest
  const redoStackRef = useRef([]);
  const lastPushAtRef = useRef(0);
  const [, setHistoryTick] = useState(0);
  const bumpHistoryTick = () => setHistoryTick((n) => (n + 1) & 0xffff);

  const commitDrawings = useCallback((next, options = {}) => {
    // `next` can be either a new array OR a functional updater — mirror
    // setState semantics so callers don't need to change.
    setDrawings((prev) => {
      const newDrawings = typeof next === "function" ? next(prev) : next;
      if (!options.skipUndo) {
        // Time-coalesce: if the previous push was very recent (e.g. a
        // mid-drag rapid burst), REPLACE the top-of-stack instead of
        // pushing a new entry. The drag's START state survives; the
        // intermediate ticks are folded. Exactly one undo step per
        // gesture regardless of how many mousemove events fired.
        const now = Date.now();
        const stack = undoStackRef.current;
        const snapshot = prev; // pre-mutation state
        if (now - lastPushAtRef.current < COALESCE_MS && stack.length > 0) {
          // Replace top — keep the EARLIEST snapshot of the burst.
          // Do nothing: the top already holds the burst-start state.
        } else {
          stack.push(snapshot);
          if (stack.length > UNDO_LIMIT) stack.shift(); // FIFO
        }
        lastPushAtRef.current = now;
        // Any new mutation invalidates the redo branch.
        redoStackRef.current = [];
        bumpHistoryTick();
      }
      return newDrawings;
    });
  }, []);

  // Public undo/redo entry points used by the keyboard handler.
  const undoLastDrawingChange = useCallback(() => {
    const undoStack = undoStackRef.current;
    if (undoStack.length === 0) {
      toast(tLm(lang).patterns?.drawing?.undo_empty || "No hay más pasos para deshacer", {
        duration: 1500,
      });
      return;
    }
    setDrawings((current) => {
      const prev = undoStack.pop();
      redoStackRef.current.push(current);
      if (redoStackRef.current.length > UNDO_LIMIT) redoStackRef.current.shift();
      lastPushAtRef.current = 0; // break coalesce after undo
      bumpHistoryTick();
      return prev;
    });
  }, [lang]);

  const redoLastDrawingChange = useCallback(() => {
    const redoStack = redoStackRef.current;
    if (redoStack.length === 0) {
      toast(tLm(lang).patterns?.drawing?.redo_empty || "No hay más pasos para rehacer", {
        duration: 1500,
      });
      return;
    }
    setDrawings((current) => {
      const next = redoStack.pop();
      undoStackRef.current.push(current);
      if (undoStackRef.current.length > UNDO_LIMIT) undoStackRef.current.shift();
      lastPushAtRef.current = 0;
      bumpHistoryTick();
      return next;
    });
  }, [lang]);

  // Keyboard shortcuts: Ctrl+Z (undo), Ctrl+Y / Ctrl+Shift+Z (redo).
  // Mac users get Cmd+Z / Cmd+Shift+Z via metaKey. We deliberately skip
  // when the focus is in an editable element so a text input doesn't
  // lose its own browser-native undo to ours.
  useEffect(() => {
    function isEditingText(el) {
      if (!el) return false;
      const tag = (el.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return true;
      if (el.isContentEditable) return true;
      return false;
    }
    function handler(e) {
      if (!(e.ctrlKey || e.metaKey)) return;
      if (isEditingText(document.activeElement)) return;
      const key = (e.key || "").toLowerCase();
      if (key === "z" && !e.shiftKey) {
        e.preventDefault();
        undoLastDrawingChange();
      } else if (key === "y" || (key === "z" && e.shiftKey)) {
        e.preventDefault();
        redoLastDrawingChange();
      }
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [undoLastDrawingChange, redoLastDrawingChange]);
  // Persist drawings per (coin, timeframe). We use a ref to gate the save
  // effect: it MUST NOT fire until we have explicitly loaded the persisted
  // value for the CURRENT (coin, timeframe). Without this guard, the
  // initial render fires the save effect with the React-default state ([])
  // and overwrites the persisted localStorage entry BEFORE the load effect
  // has had a chance to inject it. The ref tracks "what (coin, tf) is the
  // current `drawings` value associated with"; the save effect only writes
  // when this matches the current props.
  const loadedKeyRef = useRef(`${coin}|${timeframe}`);
  useEffect(() => {
    const key = `${coin}|${timeframe}`;
    // Always reload from storage on coin/tf change — even when the previous
    // key was the same combo (e.g. defensive remount). Cheap (sync JSON parse).
    // B7c: hydration is NOT a user action — does NOT enter the undo stack.
    setDrawings(loadDrawings(coin, timeframe));
    // Reset history on (coin, tf) switch — drawings from the previous
    // pair are no longer reachable so their undo entries make no sense.
    undoStackRef.current = [];
    redoStackRef.current = [];
    lastPushAtRef.current = 0;
    bumpHistoryTick();
    loadedKeyRef.current = key;
    setActiveTool("cursor");
  }, [coin, timeframe]);
  useEffect(() => {
    const key = `${coin}|${timeframe}`;
    // Save is skipped while the load for the current key is still pending —
    // i.e. when this effect's coin/tf doesn't match what was last loaded.
    // This prevents wiping a freshly mounted key with the old state's data.
    if (loadedKeyRef.current !== key) return;
    saveDrawings(coin, timeframe, drawings);
  }, [drawings, coin, timeframe]);
  const [srData, setSrData] = useState(null);
  const [srLoading, setSrLoading] = useState(false);
  const [srError, setSrError] = useState(null);
  // Count of "🔒 + ≥80% bounce + ≥5 touches" setups in the current SR payload.
  // Drives the animated red dot on the `📐 S/R IA` button + the success toast.
  const [srHighProb, setSrHighProb] = useState(0);
  // Dedupe set so the background radar never re-toasts the same setup twice
  // within a coin+timeframe session. Cleared when the user switches coin/TF.
  const srAlertedRef = useRef(new Set());
  // Recent-setups panel state.
  const [srRecentOpen, setSrRecentOpen] = useState(false);
  const [srRecentData, setSrRecentData] = useState(null);
  const [srRecentLoading, setSrRecentLoading] = useState(false);
  const [srRecentCoin, setSrRecentCoin] = useState(null); // null = all coins
  // Cascade Predictor "Predicciones Recientes" drawer.
  const [cascadeRecentOpen, setCascadeRecentOpen] = useState(false);
  // Cascade Timer "Histórico" drawer (P1 — Decisión #4 suppresses stats <30).
  const [cascadeTimerHistoryOpen, setCascadeTimerHistoryOpen] = useState(false);
  // Cross-coin S/R Top Levels drawer (Fase 2, 2026-02).
  const [srTopLevelsOpen, setSrTopLevelsOpen] = useState(false);

  // ─── Local i18n for legend (kept inline per "only LiqMapPage" constraint) ───
  const LEGEND_T = {
    es: {
      title: "GUÍA DEL MAPA DE LIQUIDACIONES",
      colors: [
        ["Sin actividad — zona sin posiciones apalancadas significativas", [0, 0, 50]],
        ["Baja concentración — pocas posiciones en riesgo de liquidación", [0, 80, 40]],
        ["Concentración media — zona de acumulación moderada de posiciones", [20, 140, 0]],
        ["Alta concentración — zona importante, muchos stops en este nivel", [180, 180, 0]],
        ["Concentración muy alta — zona crítica de stop-loss y liquidaciones masivas", [255, 90, 0]],
        ["Concentración máxima — cluster principal, precio magnético del mercado", [220, 10, 10]],
        ["Cluster extremo — mayor densidad de posiciones apalancadas del período", [255, 240, 60]],
      ],
      max: ["MAX", "Nivel con mayor concentración de liquidaciones del período"],
      sup: ["SUP", "Segundo nivel más importante de liquidaciones"],
      live: "Precio actual en tiempo real",
      footer: (n) => `Datos agregados de ${n} exchanges en tiempo real`,
      help: "?",
    },
    en: {
      title: "LIQUIDATION MAP GUIDE",
      colors: [
        ["No activity — no significant leveraged positions in this zone", [0, 0, 50]],
        ["Low concentration — few positions at liquidation risk", [0, 80, 40]],
        ["Medium concentration — moderate position accumulation zone", [20, 140, 0]],
        ["High concentration — important zone, many stops at this level", [180, 180, 0]],
        ["Very high concentration — critical stop-loss and mass-liquidation zone", [255, 90, 0]],
        ["Maximum concentration — main cluster, magnetic price for the market", [220, 10, 10]],
        ["Extreme cluster — highest density of leveraged positions in the period", [255, 240, 60]],
      ],
      max: ["MAX", "Level with the highest liquidation concentration of the period"],
      sup: ["SUP", "Second most important liquidation level"],
      live: "Live current price",
      footer: (n) => `Aggregated data from ${n} live exchanges`,
      help: "?",
    },
    sk: {
      title: "SPRIEVODCA MAPOU LIKVIDÁCIÍ",
      colors: [
        ["Žiadna aktivita — zóna bez významných pákových pozícií", [0, 0, 50]],
        ["Nízka koncentrácia — málo pozícií v riziku likvidácie", [0, 80, 40]],
        ["Stredná koncentrácia — zóna miernej akumulácie pozícií", [20, 140, 0]],
        ["Vysoká koncentrácia — dôležitá zóna, mnoho stopov na tejto úrovni", [180, 180, 0]],
        ["Veľmi vysoká koncentrácia — kritická zóna stop-loss a hromadných likvidácií", [255, 90, 0]],
        ["Maximálna koncentrácia — hlavný klaster, magnetická cena trhu", [220, 10, 10]],
        ["Extrémny klaster — najvyššia hustota pákových pozícií obdobia", [255, 240, 60]],
      ],
      max: ["MAX", "Úroveň s najvyššou koncentráciou likvidácií obdobia"],
      sup: ["SUP", "Druhá najdôležitejšia úroveň likvidácií"],
      live: "Aktuálna cena v reálnom čase",
      footer: (n) => `Agregované dáta z ${n} búrz v reálnom čase`,
      help: "?",
    }, de: { title: "LEITFADEN ZUR LIQUIDATIONSKARTE", colors: [["Keine Aktivität — Zone ohne signifikante gehebelte Positionen", [0, 0, 50]], ["Geringe Konzentration — wenige Positionen mit Liquidationsrisiko", [0, 80, 40]], ["Mittlere Konzentration — Zone mit moderater Positionsakkumulation", [20, 140, 0]], ["Hohe Konzentration — wichtige Zone, viele Stops auf diesem Niveau", [180, 180, 0]], ["Sehr hohe Konzentration — kritische Zone für Stop-Loss und Massenliquidationen", [255, 90, 0]], ["Maximale Konzentration — Haupt-Cluster, magnetischer Preis des Marktes", [220, 10, 10]], ["Extremer Cluster — höchste Dichte gehebelter Positionen des Zeitraums", [255, 240, 60]]], max: ["MAX", "Niveau mit der höchsten Liquidationskonzentration des Zeitraums"], sup: ["SUP", "Zweitwichtigstes Liquidationsniveau"], live: "Aktueller Preis in Echtzeit", footer: (n) => `Aggregierte Daten von ${n} Börsen in Echtzeit`, help: "?" }, "zh-CN": { title: "强制平仓地图指南", colors: [["无活动 — 无显著杠杆仓位区域", [0, 0, 50]], ["低集中度 — 少量面临强制平仓风险的仓位", [0, 80, 40]], ["中等集中度 — 仓位适度累积区域", [20, 140, 0]], ["高集中度 — 重要区域，该价位有大量止损", [180, 180, 0]], ["极高集中度 — 关键区域，大规模止损和强制平仓", [255, 90, 0]], ["最大集中度 — 主要 Cluster，市场磁性价格", [220, 10, 10]], ["极端 Cluster — 该时段杠杆仓位密度最高", [255, 240, 60]]], max: ["MAX", "该时段强制平仓集中度最高的价位"], sup: ["SUP", "第二重要的强制平仓价位"], live: "实时当前价格", footer: (n) => `来自 ${n} 家交易所的实时聚合数据`, help: "?" },
  };
  const tL = LEGEND_T[lang] || LEGEND_T.en || LEGEND_T.es;

  // ─── Parse URL params on first mount — pre-fill simulator from shared link ──
  // Shape: /liquidity-map?sim=1&coin=BTC&entry=67000&lev=50&dir=LONG&size=1000
  // Read once into the ref; the auto-calc effect below re-applies as soon as
  // the snapshot loads (state-setters here may race the snapshot fetch).
  const readSharedFromUrl = () => {
    try {
      const sp = new URLSearchParams(window.location.search);
      if (sp.get("sim") !== "1") return null;
      const c = (sp.get("coin") || "").toUpperCase();
      const e = parseFloat(sp.get("entry") || "");
      const lv = parseFloat(sp.get("lev") || "");
      const d = (sp.get("dir") || "").toUpperCase();
      const sz = parseFloat(sp.get("size") || "");
      const valid = /^[A-Z0-9]{2,10}$/.test(c)
        && Number.isFinite(e) && e > 0
        && Number.isFinite(lv) && lv >= 2 && lv <= 125
        && (d === "LONG" || d === "SHORT");
      if (!valid) return null;
      return { coin: c, entry: e, lev: lv, dir: d, size: Number.isFinite(sz) && sz > 0 ? sz : null };
    } catch { return null; }
  };
  useEffect(() => {
    if (sharedParamsRef.current) return; // run once
    const shared = readSharedFromUrl();
    if (!shared) return;
    sharedParamsRef.current = shared;
    setCoin(shared.coin);
    setSimEntryInput(String(shared.entry));
    setSimLeverage(Math.round(shared.lev));
    setSimDirection(shared.dir);
    if (shared.size != null) setSimSizeInput(String(shared.size));
    setSimOpen(true);
  }, []);

  // ─── Drawings share permalink (#draw=...) — hydrate on first mount,
  //     merge into current drawings if hash present. Switch coin/tf to
  //     match the shared payload so the drawings render in their
  //     intended chart-space context. Runs once. ───────────────────────
  const drawShareHydratedRef = useRef(false);
  useEffect(() => {
    if (drawShareHydratedRef.current) return;
    if (typeof window === "undefined") return;
    const shared = parseShare(window.location.hash || "");
    if (!shared || !Array.isArray(shared.drawings) || shared.drawings.length === 0) return;
    drawShareHydratedRef.current = true;
    if (shared.coin !== coin) setCoin(shared.coin);
    if (shared.timeframe !== timeframe) setTimeframe(shared.timeframe);
    // Defer the merge so the (coin, tf) load effect has time to fire
    // first — otherwise our merge would be overwritten by loadDrawings.
    setTimeout(() => {
      commitDrawings((existing) => {
        const seen = new Set(existing.map((d) => d.id));
        const fresh = shared.drawings.filter((d) => d && d.id && !seen.has(d.id));
        return [...existing, ...fresh];
      });
      try {
        toast.success(
          (tLm(lang).patterns?.drawing?.share_imported || "{n} dibujos importados").replace("{n}", shared.drawings.length)
        );
      } catch { /* sonner not ready */ }
      // Strip the #draw= fragment so subsequent reloads don't re-import.
      try { history.replaceState(null, "", window.location.pathname + window.location.search); } catch { /* ignore */ }
    }, 200);
    // Intentional: we don't depend on coin/timeframe here; we want a
    // single-shot hydration on initial mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Build and copy a #draw=... permalink with the current drawings ─
  const onShareDrawings = async () => {
    if (typeof window === "undefined") return;
    if (!drawings || drawings.length === 0) {
      toast.error(tLm(lang).patterns?.drawing?.share_empty || "No hay dibujos para compartir");
      return;
    }
    const payload = serializeShare(coin, timeframe, drawings);
    const url = `${window.location.origin}${window.location.pathname}#draw=${payload}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(
        (tLm(lang).patterns?.drawing?.share_copied || "Permalink copiado ({n} dibujos · {kb} KB)")
          .replace("{n}", drawings.length)
          .replace("{kb}", (url.length / 1024).toFixed(1))
      );
    } catch {
      // Clipboard blocked → fall back to a prompt so the user can copy manually.
      // eslint-disable-next-line no-alert
      window.prompt(tLm(lang).patterns?.drawing?.share_fallback || "Copiá este enlace:", url);
    }
  };

  // ─── B-Shadow — capture chart as PNG and open PublishAnalysisModal ─
  //
  // Strategy: we DON'T mount the watermark as a React node. Instead we
  // imperatively append a transparent <img> into the heatmap root just
  // before calling htmlToImage.toBlob, then remove it. This guarantees
  // the watermark is never visible during normal use of the chart
  // (no opacity flicker, no CSS toggle race) and the file always shows
  // the brand. The watermark width is `12%` of the heatmap container
  // (NOT a fixed px count) and height is `auto` so the native 5:1
  // aspect ratio of /assets/branding/rebel_watermark.png is preserved
  // regardless of viewport.
  const [shadowOpenSignal, setShadowOpenSignal] = useState(0);
  const [shadowPrefill, setShadowPrefill] = useState(null);
  const FREE_CHARS = 500;
  const PRO_CHARS = 5000;

  const onShareToShadow = async () => {
    if (!user) {
      try { openLogin && openLogin(); } catch { /* ignore */ }
      return;
    }
    const target = typeof document !== "undefined"
      ? document.querySelector('[data-testid="liqmap-heatmap-root"]')
      : null;
    if (!target) {
      toast.error("Chart no listo todavía");
      return;
    }
    // Capture via the shared helper — same watermark + htmlToImage
    // pipeline now lives in /lib/captureChart.js so CyclesMapView can
    // reuse it without re-implementing.
    const blob = await captureChartAsBlob({
      target,
      watermarkUrl: rebelWatermarkUrl,
    });
    if (!blob) {
      toast.error("No se pudo capturar el chart");
      return;
    }
    // Derive direction from the current max-cluster tipo (the cascade
    // direction shown on the chart): "LONGS" → SHORT, "SHORTS" → LONG.
    const tipoStr = String(cluster?.maxTipo || "").toUpperCase();
    let direction = "NEUTRAL";
    if (tipoStr.includes("LONGS")) direction = "SHORT";
    else if (tipoStr.includes("SHORTS")) direction = "LONG";
    // Build base description (auto-filled, fully editable in the modal).
    const baseDesc = `📊 Análisis ${coin} · ${timeframe}\n${direction !== "NEUTRAL" ? `🎯 Dirección esperada: ${direction}\n` : ""}`;
    // Permalink: include only if drawings exist AND it fits the tier cap.
    let finalDesc = baseDesc;
    if (Array.isArray(drawings) && drawings.length > 0) {
      const payload = serializeShare(coin, timeframe, drawings);
      const linkLine = `\n\n🔗 Ver análisis interactivo: ${window.location.origin}${window.location.pathname}#draw=${payload}`;
      const maxChars = isPro ? PRO_CHARS : FREE_CHARS;
      if ((baseDesc.length + linkLine.length) <= maxChars) {
        finalDesc = baseDesc + linkLine;
      } else {
        // Spec: NEVER truncate user content. Just notify and omit.
        toast.message("El permalink no cupo por el límite de caracteres del plan — se omitió del post.");
      }
    }
    setShadowPrefill({
      image: blob,
      title: "",
      description: finalDesc,
      ticker: coin,
      timeframe,
      direction,
    });
    setShadowOpenSignal((v) => v + 1);
    // Best-effort: offer native share sheet on mobile when available
    // (helper is fire-and-forget; the modal remains the canonical path).
    tryNativeShare(blob, { title: `${coin} ${timeframe}` });
  };

  // ─── Open login modal if unauthenticated (skip if token already cached) ───
  const openedRef = useRef(false);
  useEffect(() => {
    if (authLoading) return;
    // If a JWT is stored we'll be hydrated shortly via /auth/me; don't flash the modal.
    let cachedToken = null;
    try { cachedToken = localStorage.getItem("rebel_jwt"); } catch { /* ignore */ }
    if (!user && !cachedToken && !openedRef.current) {
      openedRef.current = true;
      openLogin?.();
    }
  }, [user, authLoading, openLogin]);

  // ─── Activate trial + fetch initial status ───
  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    (async () => {
      try {
        // First read status. If has_started=false → activate. PRO bypass anyway.
        const r = await fetch(`${API}/liqmap/trial/status`, { headers });
        let s = await r.json();
        if (s && !s.has_started && !s.is_pro) {
          const r2 = await fetch(`${API}/liqmap/trial/activate`, { method: "POST", headers });
          s = await r2.json();
        }
        setStatus(s);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("liqmap status err", e);
      }
    })();
  }, [token]);

  // ─── Reset category filter when the coin changes (per UX spec) ───
  useEffect(() => { setCategory("all"); }, [coin]);

  // ─── Fetch snapshot + candles whenever coin/timeframe/category change (and access ok) ───
  const hasAccess = !!status && (status.is_pro || (status.has_started && !status.is_expired));

  // ─── CEX/DEX divergence (2026-02 FASE 3) ──────────────────────────────
  // The backend's main snapshot now exposes `cex_cluster_max_price` and
  // `dex_cluster_max_price` alongside `cluster_max_price`, so the legacy
  // useDivergence hook (which fired 2 extra parallel fetches every 60s)
  // has been removed. We derive the divergence locally from the live
  // snapshot — same shape `{cexMax, dexMax, pct} | null` that StatsBar
  // already consumes, no contract change. Returns null when the backend
  // could not compute both sides (e.g. the user is on a `category=cex`
  // filtered view where DEX exchanges were excluded from the fetch).
  const divergence = useMemo(() => {
    const s = snapshot?.stats || {};
    const cexMax = Number(s.cex_cluster_max_price);
    const dexMax = Number(s.dex_cluster_max_price);
    if (!cexMax || !dexMax || isNaN(cexMax) || isNaN(dexMax)) return null;
    const pct = (Math.abs(dexMax - cexMax) / cexMax) * 100;
    return { cexMax, dexMax, pct };
  }, [snapshot]);

  useEffect(() => {
    if (!token || !hasAccess) return;
    let alive = true;
    const headers = { Authorization: `Bearer ${token}` };
    setLoading(true);
    setErr(null);
    Promise.all([
      fetch(`${API}/liqmap/${coin}?category=${category}`, { headers }).then((r) => r.json()),
      fetch(`${API}/liqmap/candles/${coin}/${timeframe}?limit=60`, { headers }).then((r) => r.json()),
    ])
      .then(([snap, ckRes]) => {
        if (!alive) return;
        setSnapshot(snap);
        setCandles(Array.isArray(ckRes?.candles) ? ckRes.candles : []);
        // Backend emits `stale:true` + `age_s` only when serving from
        // last-known-good cache because OKX AND Gate.io both failed.
        // Fresh responses set stale to false; we mirror that to clear
        // any prior badge as soon as upstream recovers.
        setCandlesStale(ckRes?.stale ? { age_s: Number(ckRes?.age_s) || 0 } : null);
        // Snapshot stale flag — set by GET /api/liqmap/{coin} 5-minute
        // lookback when the latest doc had error / empty clusters.
        // Surfaces a SEPARATE badge from the candles one because the
        // two outages are independent (snapshot scheduler aggregates
        // exchange OI/funding/etc.; candles is a direct OKX/Gate proxy).
        setSnapshotStale(snap?.stale ? { age_s: Number(snap?.stale_age_s) || 0 } : null);
        if (snap?.error === "insufficient_exchanges") setErr(t.err_insufficient);
        setLoading(false);
      })
      .catch(() => {
        if (!alive) return;
        setErr(t.err_load);
        setLoading(false);
      });
    return () => { alive = false; };
  }, [coin, timeframe, category, token, hasAccess, t]);

  // ─── Auto refresh snapshot every 30s ───
  useEffect(() => {
    if (!token || !hasAccess) return;
    const id = setInterval(() => {
      fetch(`${API}/liqmap/${coin}?category=${category}`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.json())
        .then((s) => setSnapshot((prev) => (s?.error ? prev : s)))
        .catch(() => {});
    }, 30_000);
    return () => clearInterval(id);
  }, [coin, category, token, hasAccess]);

  // ─── Wyckoff Structure — lightweight badge state, synced to chart tf.
  // Fetched on coin/timeframe change + every 90s. Drawer opens with the
  // same tf, and can be switched independently inside the drawer.
  useEffect(() => {
    if (!token || !hasAccess || !coin) return;
    const badgeTf = ["15m", "1h", "4h", "1d"].includes(timeframe) ? timeframe : "1h";
    let alive = true;
    const fetchBadge = () => {
      fetch(`${API}/liqmap/wyckoff/${coin}?tf=${badgeTf}`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.ok ? r.json() : null)
        .then((j) => {
          if (!alive || !j) return;
          setStructureBadge({
            is_ranging: !!j.range?.is_ranging,
            tf: badgeTf,
            has_recent_event: (j.recent_events || []).length > 0,
            events_count: (j.recent_events || []).length,
            duration_hours: j.range?.duration_hours || null,
          });
        })
        .catch(() => {});
    };
    fetchBadge();
    const id = setInterval(fetchBadge, 90_000);
    return () => { alive = false; clearInterval(id); };
  }, [coin, timeframe, token, hasAccess]);

  // ─── WebSocket live price (OKX-backed via backend proxy) ───
  useEffect(() => {
    setLivePrice(null);  // reset stale price when the coin changes
    if (!hasAccess || !WS_URL) return undefined;
    let ws;
    let stopped = false;
    let backoff = 1000;
    const open = () => {
      ws = new WebSocket(`${WS_URL}/api/liqmap/ws/${coin}`);
      ws.onmessage = (ev) => {
        try {
          const d = JSON.parse(ev.data);
          if (d?.price) setLivePrice(d.price);
        } catch { /* ignore parse errors */ }
      };
      ws.onclose = () => {
        if (stopped) return;
        setTimeout(open, Math.min(15000, backoff));
        backoff *= 2;
      };
      ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
    };
    open();
    return () => { stopped = true; try { ws?.close(); } catch { /* ignore */ } };
  }, [coin, hasAccess]);

  // ─── Derived stats for StatsBar ───
  // The cluster_max value comes from the single-source-of-truth hook so the
  // header stat, cascade banner card, canvas MAX label and arrow overlay
  // can never drift apart again. See /app/frontend/src/hooks/useClusterData.js
  const cluster = useClusterData(snapshot);

  const statsForBar = useMemo(() => {
    const s = snapshot?.stats || {};
    const lp = livePrice ?? s.price_avg ?? null;
    const ch24 = (typeof s.price_change_24h_pct === "number") ? s.price_change_24h_pct : null;
    // ─── Real-liq integration (Opción B, 2026-02-23) ──────────────────────
    // During the 1h collector warming window: status="warming_up" and we
    // fall back to the projected values. After warming: show real values.
    const realStatus = s.liq_real_status || null;
    const isWarming = !realStatus || realStatus === "warming_up";
    const longsReal  = Number(s.liq_longs_24h_real_usd  || 0);
    const shortsReal = Number(s.liq_shorts_24h_real_usd || 0);
    // Backward compat for projected: prefer new `_internal_*` (current name)
    // then legacy `liq_longs_projected_usd` / `liq_longs_24h`.
    const longsProj = (
      s._internal_liq_longs_projected_usd
      ?? s.liq_longs_projected_usd
      ?? s.liq_longs_24h
      ?? 0
    );
    const shortsProj = (
      s._internal_liq_shorts_projected_usd
      ?? s.liq_shorts_projected_usd
      ?? s.liq_shorts_24h
      ?? 0
    );
    return {
      price: lp,
      change24: ch24,
      openInterest: s.open_interest_total_usd,
      liqLongs:  isWarming ? longsProj  : longsReal,
      liqShorts: isWarming ? shortsProj : shortsReal,
      liqRealStatus: realStatus,
      liqRealSourcesActive: s.liq_real_sources_active || [],
      liqRealWarmingSecondsRemaining: s.liq_real_warming_seconds_remaining,
      liqRealCoverageNote: s.liq_real_coverage_note || null,
      clusterMax: cluster.maxPrice,
      defiTvlAtRisk: s.defi_tvl_at_risk || 0,
      defiProtocols: s.defi_protocols || [],
    };
  }, [snapshot, livePrice, cluster]);

  // ─── Cascade Predictor hook — owns score/data/loading state, the 3
  // trigger effects, coin-change reset, localStorage hydration/persistence.
  // See /app/frontend/src/hooks/useCascadePredictor.js for FIX A (coin-switch
  // AbortController) and FIX B (early `isPredictorRunning` flag).
  const {
    cascadeScore,
    cascadeData,
    cascadeLoading,
    bannerClosed, setBannerClosed,
    showHistory, setShowHistory,
    now,
    stickyOk,
    lastCallAt,
  } = useCascadePredictor({
    snapshot,
    livePrice,
    coin,
    token,
    hasAccess,
    cluster,
    change24: statsForBar.change24,
  });

  // Aggregated inputs for the CompositeSignalBadge in the header. Rebuilt
  // whenever any contributor changes; the memo produces a stable object
  // reference across renders so the badge's inner useEffect doesn't
  // thrash on unrelated ticks (e.g. livePrice does move a lot).
  const compositeInputs = useMemo(() => ({
    emaCrosses: emaCrossesForComposite,
    stochasticCrosses,
    candleCount: candles.length,
    clusters: snapshot?.clusters || [],
    livePrice,
    wyckoffBadge: structureBadge,
    cascadeData,
  }), [emaCrossesForComposite, stochasticCrosses, candles.length, snapshot?.clusters, livePrice, structureBadge, cascadeData]);

  // ─── Coin-change reset for non-cascade state owned by this page ──────
  // (Snapshot + cascade-timer reset. The cascade predictor handles its own
  // reset internally via the hook, including AbortController abort.)
  useEffect(() => {
    setSnapshot(null);
    setCascadeTimer(null);
    setCascadeTimerLocal(null);
  }, [coin]);

  // Effect A/B/C and `callPredictor` removed: now owned by useCascadePredictor.

  // Reset NON-cascade state when the coin changes (simulator overlay,
  // whales, S/R IA). The cascade predictor handles its own reset in the
  // hook, including AbortController abort.
  // Skip the run that follows a shared-link prefill (the prefill effect sets
  // `coin` and we don't want to wipe the simulator we just populated).
  const lastResetCoinRef = useRef(null);
  useEffect(() => {
    // First time we see this coin: just record and skip (no actual change).
    if (lastResetCoinRef.current === null) {
      lastResetCoinRef.current = coin;
      return;
    }
    // If a shared-link prefill targets this coin and hasn't been computed yet,
    // skip the reset until the auto-calc effect has applied the simulation.
    if (sharedParamsRef.current
        && sharedParamsRef.current.coin === coin
        && !sharedSimAppliedRef.current) {
      lastResetCoinRef.current = coin;
      return;
    }
    if (lastResetCoinRef.current === coin) return;
    lastResetCoinRef.current = coin;
    // Simulator overlay (markers no longer apply across coins).
    setSimResult(null);
    setSimOpen(false);
    setSimEntryInput("");
    setSimSizeInput("");
    // Whale layer overlay (old clusters don't bleed into the new coin).
    setWhaleData(null);
    // S/R IA overlay (recalculate explicitly on user toggle).
    setShowSR(false);
    setSrData(null);
    setSrError(null);
    setSrHighProb(0);
    srAlertedRef.current = new Set();
  }, [coin]);

  // Also clear S/R when the timeframe changes (different chart context).
  useEffect(() => {
    setShowSR(false);
    setSrData(null);
    setSrError(null);
    setSrHighProb(0);
    srAlertedRef.current = new Set();
  }, [timeframe]);

  // ─── High-probability setup detector — pure helper, reused by the toggle
  // and by the background radar. Returns the count of hits and fires a toast
  // for any NEW hit (dedup'd via `srAlertedRef`). `source` is just a label
  // included in console logs for debugging.
  const evaluateSrHighProb = (data, { silent = false } = {}) => {
    if (!data) return 0;
    const isHighProb = (lv) =>
      lv && lv.confirmado_por_liquidaciones
      && Number(lv.tasa_acierto_pct) >= 80
      && Number(lv.num_toques) >= 5;
    const hits = [
      ...(data.resistencias || []).filter(isHighProb).map((lv) => ({ ...lv, kind: "R" })),
      ...(data.soportes || []).filter(isHighProb).map((lv) => ({ ...lv, kind: "S" })),
    ];
    if (silent || hits.length === 0) return hits.length;
    // Dedup: key = coin:timeframe:kind:precio (rounded to 1 decimal).
    const newHits = hits.filter((lv) => {
      const key = `${data.coin || coin}:${data.timeframe || timeframe}:${lv.kind}:${Number(lv.precio).toFixed(1)}`;
      if (srAlertedRef.current.has(key)) return false;
      srAlertedRef.current.add(key);
      return true;
    });
    if (newHits.length > 0) {
      const top = newHits.sort((a, b) => b.tasa_acierto_pct - a.tasa_acierto_pct)[0];
      const fmt = (n) => n >= 1000 ? `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : `$${n.toFixed(2)}`;
      const labelKind = top.kind === "R" ? "Resistencia" : "Soporte";
      toast(`🎯 Setup alta probabilidad · ${labelKind} ${fmt(top.precio)}`, {
        description: `${top.tasa_acierto_pct}% éxito en ${top.num_toques} toques · confirmado por liquidaciones${newHits.length > 1 ? ` · +${newHits.length - 1} nuevos` : ""}`,
        duration: 8000,
        style: {
          background: "rgba(8,5,8,0.97)",
          border: "1px solid rgba(249,115,22,0.55)",
          color: "#f97316",
        },
      });
      // Persist every new hit to the alert history (best-effort, never blocks UI).
      if (token) {
        const dataCoin = data.coin || coin;
        const dataTf = data.timeframe || timeframe;
        const precioActual = Number(data.precio_actual) || 0;
        newHits.forEach((lv) => {
          fetch(`${API}/liqmap/sr-alerts/log`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              coin: dataCoin,
              timeframe: dataTf,
              kind: lv.kind,
              precio: Number(lv.precio),
              tasa_acierto_pct: Number(lv.tasa_acierto_pct),
              num_toques: Number(lv.num_toques),
              precio_actual_at_detection: precioActual,
            }),
          }).catch(() => { /* silent */ });
        });
      }
    }
    return hits.length;
  };

  // ─── S/R IA toggle handler — fetch + display top supports & resistances ──
  const toggleSoportesResistencias = async () => {
    if (showSR) {
      setShowSR(false);
      setSrData(null);
      setSrError(null);
      // Keep srHighProb so the badge survives — counter is refreshed by the radar.
      return;
    }
    setSrLoading(true);
    setSrError(null);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(
        `${API}/liqmap/support-resistance/${coin}?timeframe=${timeframe}`,
        { headers },
      );
      const data = await res.json();
      if (!res.ok) {
        setSrError(data?.detail || "sr_error");
        setSrData(null);
        setShowSR(false);
      } else {
        setSrData(data);
        setShowSR(true);
        const hits = evaluateSrHighProb(data);
        setSrHighProb(hits);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("S/R fetch error:", e);
      setSrError("network_error");
      setSrData(null);
      setShowSR(false);
    } finally {
      setSrLoading(false);
    }
  };

  // ─── Open the "Setups recientes" panel — fetches /sr-alerts/recent ─────
  const fetchSrRecent = async (coinFilter) => {
    setSrRecentLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const q = `days=7${coinFilter ? `&coin=${coinFilter}` : ""}`;
      const res = await fetch(`${API}/liqmap/sr-alerts/recent?${q}`, { headers });
      const data = await res.json();
      if (res.ok) setSrRecentData(data);
      else setSrRecentData({ items: [], stats: { total: 0 }, error: data?.detail });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("sr recent err:", e);
      setSrRecentData({ items: [], stats: { total: 0 }, error: "network" });
    } finally {
      setSrRecentLoading(false);
    }
  };
  const openSrRecent = async () => {
    setSrRecentOpen(true);
    setSrRecentCoin(null);
    await fetchSrRecent(null);
  };
  // Re-fetch whenever the user picks a coin chip while the modal is open.
  useEffect(() => {
    if (!srRecentOpen) return;
    fetchSrRecent(srRecentCoin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [srRecentCoin]);

  // ─── Background "radar" — every 5 min while the user has access AND the  // panel is NOT open. Fetches /support-resistance silently for the active
  // coin/timeframe and surfaces a toast for any NEW high-prob setup.
  useEffect(() => {
    if (!hasAccess || !token) return undefined;
    let stopped = false;
    let timerId = null;
    const radarTick = async () => {
      if (stopped) return;
      // Skip when the user has the panel already open — toggleSoportesResistencias
      // is the source of truth for those refreshes.
      if (showSR) return;
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await fetch(
          `${API}/liqmap/support-resistance/${coin}?timeframe=${timeframe}`,
          { headers },
        );
        if (!res.ok) return;
        const data = await res.json();
        if (stopped) return;
        const hits = evaluateSrHighProb(data);
        setSrHighProb(hits);
      } catch {
        /* silent — passive radar must never disrupt the chart */
      }
    };
    // First tick after 90s so we don't double-fire alongside the toggle.
    timerId = setTimeout(function loop() {
      radarTick();
      timerId = setTimeout(loop, 5 * 60 * 1000);
    }, 90_000);
    return () => { stopped = true; if (timerId) clearTimeout(timerId); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccess, token, coin, timeframe, showSR]);

  // ─── Whale data fetch — only when the toggle is ON. 60s refresh. ──
  useEffect(() => {
    if (!showWhales || !token || !hasAccess) {
      setWhaleData(null);
      return undefined;
    }
    let alive = true;
    const headers = { Authorization: `Bearer ${token}` };
    const load = async () => {
      setWhaleLoading(true);
      try {
        const r = await fetch(`${API}/liqmap/whales/${coin}`, { headers });
        const d = await r.json();
        if (alive) setWhaleData(d);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("whale fetch err", e);
      } finally {
        if (alive) setWhaleLoading(false);
      }
    };
    load();
    const id = setInterval(load, 60_000);
    return () => { alive = false; clearInterval(id); };
  }, [showWhales, coin, token, hasAccess]);

  // ─── Simulator helpers ───
  const computeSimulation = () => {
    const entry = Number(simEntryInput) || statsForBar.price || 0;
    const lev = Math.max(2, Math.min(125, Number(simLeverage) || 10));
    if (!entry) return null;
    const liq = simDirection === "LONG" ? entry * (1 - 1 / lev) : entry * (1 + 1 / lev);
    const dist = Math.abs(entry - liq);
    const distPct = (dist / entry) * 100;
    const size = Number(simSizeInput) || 0;
    // 100% loss at liquidation by definition. Approximation for display only.
    const loss = size > 0 ? size : 0;
    // Cluster proximity check (DANGER vs SAFE).
    const list = snapshot?.clusters || [];
    let danger = false;
    if (list.length) {
      const bucket = (snapshot?.stats?.bucket) || (entry * 0.003);
      for (const c of list) {
        if (!c?.price || c.intensity == null) continue;
        if (Math.abs(c.price - liq) <= bucket * 3 && c.intensity >= 0.6) {
          danger = true;
          break;
        }
      }
    }
    return { entry, lev, direction: simDirection, liq, dist, distPct, size, loss, danger };
  };

  const handleSimulate = () => {
    const r = computeSimulation();
    if (!r) return;
    setSimResult(r);
  };

  // ─── Auto-calc when arriving via shared link, once snapshot is loaded ──
  // Re-reads URL params if the ref is empty (defensive against StrictMode
  // re-mounts where the ref might be cleared before the prefill effect runs).
  const sharedSimAppliedRef = useRef(false);
  useEffect(() => {
    if (sharedSimAppliedRef.current) return;
    let shared = sharedParamsRef.current || readSharedFromUrl();
    if (!shared) return;
    if (!snapshot) return; // need clusters loaded so DANGER check works
    if (shared.coin !== coin) return; // wait until coin matches the shared one
    sharedSimAppliedRef.current = true;
    sharedParamsRef.current = shared;
    // Re-apply panel inputs (idempotent — safe if prefill already ran).
    setSimEntryInput(String(shared.entry));
    setSimLeverage(Math.round(shared.lev));
    setSimDirection(shared.dir);
    if (shared.size != null) setSimSizeInput(String(shared.size));
    setSimOpen(true);
    // Compute directly from ref values — bypasses any race with state setters.
    const entry = shared.entry;
    const lev = Math.max(2, Math.min(125, shared.lev));
    const direction = shared.dir;
    const liq = direction === "LONG" ? entry * (1 - 1 / lev) : entry * (1 + 1 / lev);
    const dist = Math.abs(entry - liq);
    const distPct = (dist / entry) * 100;
    const size = shared.size && shared.size > 0 ? shared.size : 0;
    const list = snapshot?.clusters || [];
    let danger = false;
    if (list.length) {
      const bucket = (snapshot?.stats?.bucket) || (entry * 0.003);
      for (const c of list) {
        if (!c?.price || c.intensity == null) continue;
        if (Math.abs(c.price - liq) <= bucket * 3 && c.intensity >= 0.6) {
          danger = true;
          break;
        }
      }
    }
    setSimResult({ entry, lev, direction, liq, dist, distPct, size, loss: size, danger });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot, coin]);

  // ─── Share — build canonical OG URL and copy to clipboard ──
  const buildShareUrl = () => {
    const entry = Number(simEntryInput) || statsForBar.price || 0;
    const lev = Math.max(2, Math.min(125, Number(simLeverage) || 10));
    if (!entry) return null;
    const params = new URLSearchParams({
      coin,
      entry: String(entry),
      lev: String(lev),
      dir: simDirection,
    });
    const sz = Number(simSizeInput);
    if (Number.isFinite(sz) && sz > 0) params.set("size", String(sz));
    return `${API_BASE}/api/og/sim?${params.toString()}`;
  };

  // ─── Build a pre-armed Tweet copy that converts curiosity into clicks ──
  const buildTweetText = () => {
    const entry = Number(simEntryInput) || statsForBar.price || 0;
    const lev = Math.max(2, Math.min(125, Number(simLeverage) || 10));
    if (!entry) return null;
    const liq = simDirection === "LONG" ? entry * (1 - 1 / lev) : entry * (1 + 1 / lev);
    const liqStr = liq >= 1000 ? `$${Math.round(liq).toLocaleString("en-US")}` : `$${liq.toFixed(2)}`;
    const tag = `$${coin}`;
    const arrow = simDirection === "LONG" ? "📉" : "📈";
    return (
      `${arrow} Mi liquidación ${tag} ${simDirection} ${Math.round(lev)}x en ${liqStr} 🔥\n\n` +
      `¿Soportaré la cascada o el mercado me come? 👇\n\n` +
      `Simula la tuya gratis en @rebelbtc:`
    );
  };

  const handleShare = async () => {
    // If user hasn't computed yet, do so first so the result is visible too.
    if (!simResult) handleSimulate();
    const url = buildShareUrl();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch {
      window.prompt("Copia el enlace:", url);
    }
  };

  const handleShareTwitter = () => {
    // Compute first if user hasn't yet — so the canvas matches what they share.
    if (!simResult) handleSimulate();
    const url = buildShareUrl();
    const text = buildTweetText();
    if (!url || !text) return;
    const intent =
      `https://twitter.com/intent/tweet` +
      `?text=${encodeURIComponent(text)}` +
      `&url=${encodeURIComponent(url)}`;
    window.open(intent, "_blank", "noopener,noreferrer,width=560,height=520");
  };

  const closeSimulator = () => {
    setSimOpen(false);
    setSimResult(null);
  };

  // Tick "actualizado hace Xs" — now handled inside useCascadePredictor.

  // ─── FEATURE 2 — Cascade-timer fetcher (every 60s) ───────────────────
  // Independent of the predictor; reads the read-only endpoint we just
  // exposed. Fires only when we have access + a token. The timer is
  // visually surfaced inside the existing predictor banner below.
  useEffect(() => {
    if (!hasAccess || !token) return undefined;
    let cancelled = false;
    const fetchTimer = async () => {
      try {
        const r = await fetch(`${API}/liqmap/cascade-timer/${coin}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        setCascadeTimer(data);
        if (typeof data?.tiempo_estimado_min === "number") {
          setCascadeTimerLocal(data.tiempo_estimado_min);
        } else {
          setCascadeTimerLocal(null);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("cascade-timer fetch err", e);
      }
    };
    fetchTimer();
    const id = setInterval(fetchTimer, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [coin, hasAccess, token]);

  // ─── FEATURE 2 — Local 1-second ticker that decrements the countdown
  // without re-fetching the endpoint. The fetcher above re-syncs every 60s.
  useEffect(() => {
    if (cascadeTimerLocal == null) return undefined;
    if (!cascadeTimer?.countdown_activo) return undefined;
    const id = setInterval(() => {
      setCascadeTimerLocal((prev) => (prev == null ? null : Math.max(0, prev - 1 / 60)));
    }, 1000);
    return () => clearInterval(id);
  }, [cascadeTimerLocal != null, cascadeTimer?.countdown_activo]);

  // ─── Institutional cluster validation auto-toast (2026-02) ───
  // When `cluster_max_confidence` (or `cluster_sup_confidence`) crosses
  // ≥0.5, we show a one-shot toast with an X/Twitter share action. The
  // dedupe key is (coin · timeframe · rounded_price · side) so the toast
  // never repeats for the same validated cluster during the session, but
  // does fire again if the user switches coin/tf or the cluster moves.
  const VALIDATION_THRESHOLD = 0.5;
  useEffect(() => {
    const s = snapshot?.stats;
    if (!s) return;
    const refs = Array.isArray(s.institutional_refinements) ? s.institutional_refinements : [];
    if (refs.length < 5) return;  // backend without Fase 2 — skip silently
    const candidates = [
      {
        side: "max",
        label: "MAX",
        price: cluster.maxPrice,
        conf: cluster.maxConfidence,
        tipo: cluster.maxTipo,
      },
      {
        side: "sup",
        label: "SUP",
        price: cluster.supPrice,
        conf: cluster.supConfidence,
        tipo: cluster.supTipo,
      },
    ];
    for (const c of candidates) {
      if (!c.price || c.conf < VALIDATION_THRESHOLD) continue;
      const dedupeKey = `${coin}:${timeframe}:${c.side}:${Math.round(c.price)}`;
      if (toastedValidations.current.has(dedupeKey)) continue;
      toastedValidations.current.add(dedupeKey);
      const confPct = Math.round(c.conf * 100);
      const maxV = typeof s.consensus_max_exchanges === "number" ? s.consensus_max_exchanges : 0;
      const realLiqs = typeof s.real_liqs_count === "number" ? s.real_liqs_count : 0;
      const priceFmt = c.price.toLocaleString("en-US", { maximumFractionDigits: c.price > 1000 ? 0 : 2 });
      const directionEmoji = (c.tipo || "").includes("LONGS") ? "📉" : "📈";
      // Copy revised 2026-06-25 (Issue 2 follow-up). `cluster_max_confidence`
      // measures STRUCTURAL data quality of the cluster zone (concentration
      // of historical liquidations within ±2.5%), not whether a future
      // cascade has been verified to occur. New copy reflects that:
      //   • "validado" → "calidad validada por liquidez histórica"
      //   • "confidence" → "calidad" (when applied to the cluster, not a forecast)
      //   • "liqs reales" → "liqs reales registradas" (past, not predictive)
      // With copy honest, the X share action is restored.
      const tweetText =
        `🏛️ Cluster ${c.label} — ${coin} · ${hc.quality_validated_by_hist_liq}\n\n` +
        `${directionEmoji} $${priceFmt} · ${confPct}% ${hc.zone_quality}\n` +
        `📊 ${maxV} venues · ${realLiqs.toLocaleString()} ${hc.real_liqs_recorded}\n\n` +
        `Liquidity Map @rebelbtc\n#${coin} #Liquidations $${coin}`;
      const shareUrl = `https://rebelbtc.com/liquidity-map?coin=${coin}&tf=${timeframe}&cluster=${c.side}&p=${Math.round(c.price)}`;
      toast.success(
        `🏛️ Cluster ${c.label} — $${priceFmt}`,
        {
          description: `${confPct}% ${hc.zone_quality} · ${maxV} venues · ${realLiqs.toLocaleString()} ${hc.real_liqs_recorded}`,
          duration: 12000,
          action: {
            label: `🐦 ${hc.share_on_x}`,
            onClick: () => {
              const intent =
                `https://twitter.com/intent/tweet` +
                `?text=${encodeURIComponent(tweetText)}` +
                `&url=${encodeURIComponent(shareUrl)}`;
              window.open(intent, "_blank", "noopener,noreferrer,width=560,height=520");
            },
          },
          // Single data-testid for QA — sonner spreads unknown props to the wrapper.
          "data-testid": `cluster-validation-toast-${c.side}`,
        },
      );
    }
  }, [snapshot, coin, timeframe]);

  // Reset dedupe set when coin or timeframe changes so a fresh combo can
  // re-emit the toast if a cluster is validated again.
  useEffect(() => {
    toastedValidations.current.clear();
  }, [coin, timeframe]);

  // ─── Close the legend on click-outside or Escape ───
  useEffect(() => {
    if (!legendOpen) return undefined;
    const onDown = (e) => {
      if (legendRef.current?.contains(e.target)) return;
      if (legendBtnRef.current?.contains(e.target)) return;
      setLegendOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setLegendOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [legendOpen]);

  const trialExpired = !!status && !status.is_pro && status.has_started && status.is_expired;
  const sources = snapshot?.sources || snapshot?.stats?.sources || [];

  if (!authLoading && !user) {
    // Guest gate: for unauthenticated visitors we prefer an explicit
    // signup/login landing over the previous auto-modal + infinite
    // spinner combo. Users who dismissed the modal (or never had it
    // appear on mobile due to focus-trap edge cases) used to be stuck
    // on the "Loading…" spinner forever; now they see the CTAs and
    // can pick "Create free account" (default primary) or "I already
    // have an account" — matching the app's brand palette.
    const guestT = tLm(lang);
    return (
      <div className="min-h-screen bg-[#080508] text-white" data-testid="liqmap-guest-gate">
        <div className="mx-auto flex min-h-screen max-w-xl flex-col items-center justify-center gap-6 px-6 text-center">
          <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-white/60">
            <span className="h-2 w-2 rounded-full bg-orange-400 shadow-[0_0_10px_rgba(251,146,60,0.9)]" />
            {guestT.title || "Liquidity Map"}
          </div>
          <h1 className="text-3xl font-black leading-tight sm:text-4xl">
            {guestT.guest_title || "Necesitas una cuenta gratis"}
          </h1>
          <p className="max-w-md text-sm leading-relaxed text-white/60 sm:text-base">
            {guestT.guest_sub || "Liquidity Map muestra clusters de liquidación en tiempo real."}
          </p>
          <div className="mt-2 flex w-full flex-col items-stretch gap-3 sm:flex-row sm:justify-center">
            <button
              type="button"
              data-testid="liqmap-guest-register"
              onClick={() => {
                try { openRegister ? openRegister() : openLogin?.(); } catch { /* ignore */ }
              }}
              className="group relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-full bg-gradient-to-r from-orange-500 to-amber-600 px-7 py-3 text-sm font-bold text-black transition-all hover:-translate-y-0.5 hover:shadow-[0_0_40px_rgba(255,140,0,0.55)] sm:text-base"
            >
              <span className="relative z-10">{guestT.guest_cta_register || "Crear cuenta gratis"}</span>
              <span className="relative z-10 transition-transform group-hover:translate-x-1">→</span>
              <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
            </button>
            <button
              type="button"
              data-testid="liqmap-guest-login"
              onClick={() => { try { openLogin?.(); } catch { /* ignore */ } }}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-7 py-3 text-sm font-semibold text-white/80 transition-all hover:-translate-y-0.5 hover:border-white/30 hover:bg-white/10 sm:text-base"
            >
              {guestT.guest_cta_login || "Ya tengo cuenta"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative min-h-screen overflow-hidden text-white"
      style={{ background: "#080508" }}
      data-testid="liqmap-page"
    >
      {/* Network particles */}
      <ParticlesBackground />

      {/* Topbar */}
      <header
        className="relative z-20 flex items-center justify-between border-b px-4 py-3 backdrop-blur-md sm:px-6"
        style={{ background: "rgba(8,5,8,0.92)", borderColor: "rgba(249,115,22,0.2)" }}
      >
        <div className="flex items-center gap-3">
          <a
            href="/"
            aria-label="Back"
            data-testid="liqmap-back-btn"
            className="inline-flex items-center justify-center rounded-lg border border-white/[0.08] bg-transparent px-2.5 py-1.5 text-[#888] transition-all duration-200 hover:border-[#f97316]/40 hover:text-[#f97316]"
          >
            <ArrowLeft size={16} />
          </a>
          <a href="/" className="flex items-center gap-2 outline-none">
            <img src={ASSETS.logos.rebel} alt="RE₿EL" className="h-7 w-auto" />
          </a>
        </div>
        <h1
          className="hidden font-black sm:block"
          style={{ color: "#a855f7", letterSpacing: "3px", fontWeight: 700 }}
          data-testid="liqmap-title"
        >
          {t.title.toUpperCase()}
        </h1>
        <div className="flex items-center gap-2">
          <TrialBadge status={status} />
          <LiqScorePill coin={coin} />
          <CompositeSignalBadge inputs={compositeInputs} lang={lang} isPro={!!isPro} />
        </div>
      </header>

      {/* Body */}
      <main className="relative z-10 mx-auto max-w-7xl px-4 py-5 sm:px-6">
        {/* Controls row */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <CoinSelector value={coin} onChange={setCoin} />
          <div className="flex flex-wrap items-center gap-3">
            <TimeframeSelector value={timeframe} onChange={setTimeframe} />
            {/* Category filter — All / CEX / DEX / DeFi (DeFi added 2026-02-16) */}
            <div className="flex gap-1" data-testid="liqmap-cat-selector">
              {[
                { id: "all", label: "All", color: "#f97316" },
                { id: "cex", label: "CEX only", color: "#f97316" },
                { id: "dex", label: "DEX only", color: "#f97316" },
                { id: "defi", label: "DeFi", color: "#a855f7" },
              ].map(({ id, label, color }) => {
                const active = category === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setCategory(id)}
                    data-testid={`liqmap-cat-${id}`}
                    className={
                      "rounded-md px-2.5 py-1 font-mono text-[10px] font-bold transition-all " +
                      (active
                        ? "bg-transparent"
                        : "border border-white/10 bg-white/[0.03] text-white/40 hover:bg-white/5")
                    }
                    style={active
                      ? { border: `1px solid ${color}`, color }
                      : undefined}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            {/* Layer toggles moved inside chart container (sticky toolbar) — see liqmap-chart-toolbar. */}
            <div
              className="group relative inline-flex cursor-default items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/[0.06] px-2.5 py-1 font-mono text-[10px] font-bold text-emerald-300"
              data-testid="liqmap-live-badge"
              tabIndex={0}
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
              </span>
              {t.live} · {sources.length || 0}
              {category === "all" && snapshot?.stats?.exchanges_supported
                ? <span className="text-emerald-300/60">/{snapshot.stats.exchanges_supported}</span>
                : null}{" "}
              {category === "cex" ? "CEX" : category === "dex" ? "DEX" : category === "defi" ? "DeFi" : t.exchanges}
              {/* Hover tooltip — lists active exchanges. Pure CSS, no deps. */}
              {sources.length > 0 && (
                <div
                  className="invisible absolute right-0 top-full z-30 mt-2 w-max max-w-[280px] -translate-y-1 rounded-md border border-emerald-400/25 bg-[#0a0a0c]/95 px-3 py-2 text-left opacity-0 shadow-[0_8px_24px_-12px_rgba(16,185,129,0.4)] backdrop-blur-md transition-all duration-150 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100"
                  data-testid="liqmap-sources-tooltip"
                >
                  <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-emerald-300/70">
                    Active sources
                  </p>
                  <p className="mt-1 break-words font-mono text-[11px] leading-relaxed text-white/85">
                    {sources.join(" · ")}
                  </p>
                </div>
              )}
            </div>
            {/* Methodology badge — shown when the new direccional cluster math is active. */}
            {snapshot?.stats?.metodologia === "liquidation_price_real" && (
              <div
                className="group/method relative inline-flex items-center gap-1.5 rounded-full border border-[#f97316]/30 bg-[#f97316]/[0.06] px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[1.5px] text-[#f97316]/85"
                data-testid="liqmap-method-badge"
                tabIndex={0}
                title="Cluster calculado por precio de liquidación real (lev × OI por exchange)"
              >
                🎯 LIQ-PRICE REAL
                <div
                  className="invisible absolute right-0 top-full z-30 mt-2 w-max max-w-[300px] -translate-y-1 rounded-md border border-[#f97316]/25 bg-[#0a0a0c]/95 px-3 py-2 text-left opacity-0 shadow-[0_8px_24px_-12px_rgba(249,115,22,0.4)] backdrop-blur-md transition-all duration-150 group-hover/method:visible group-hover/method:translate-y-0 group-hover/method:opacity-100 group-focus-within/method:visible group-focus-within/method:translate-y-0 group-focus-within/method:opacity-100"
                >
                  <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#f97316]/70">
                    Metodología
                  </p>
                  <p className="mt-1 font-mono text-[10px] leading-relaxed text-white/80">
                    Cada cluster usa el precio EXACTO de liquidación
                    {" "}(<code>mark × (1 ∓ 1/lev)</code>) por nivel de
                    apalancamiento, no la concentración histórica de OI.
                    Más preciso que el método de mark-price proxy.
                  </p>
                </div>
              </div>
            )}
            {/* Institutional refinements badge — Fase 1+2 (funding skew, vol-adjusted lev,
                adaptive decay, real-liq cross-val, multi-exchange consensus). */}
            {Array.isArray(snapshot?.stats?.institutional_refinements) && snapshot.stats.institutional_refinements.length >= 5 && (() => {
              const s = snapshot.stats;
              const skew = typeof s.funding_skew_applied === "number" ? s.funding_skew_applied : null;
              const cmaxConf = typeof s.cluster_max_confidence === "number" ? s.cluster_max_confidence : 0;
              const csupConf = typeof s.cluster_sup_confidence === "number" ? s.cluster_sup_confidence : 0;
              const maxConsensus = typeof s.consensus_max_exchanges === "number" ? s.consensus_max_exchanges : 0;
              const buckets3plus = typeof s.consensus_buckets_3plus === "number" ? s.consensus_buckets_3plus : 0;
              const realLiqs = typeof s.real_liqs_count === "number" ? s.real_liqs_count : 0;
              const vol = typeof s.volatility_24h === "number" ? s.volatility_24h : null;
              const longsPct = skew != null ? Math.round(skew * 100) : null;
              const shortsPct = skew != null ? 100 - longsPct : null;
              const bestConf = Math.max(cmaxConf, csupConf);
              return (
                <div
                  className="group/inst relative inline-flex items-center gap-1.5 rounded-full border border-[#a78bfa]/40 bg-[#a78bfa]/[0.08] px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[1.5px] text-[#c4b5fd]"
                  data-testid="liqmap-institutional-badge"
                  tabIndex={0}
                  title="5 refinamientos institucionales activos: funding skew, lev por volatilidad, decay adaptativo, validación con liqs reales, consenso multi-exchange"
                >
                  🏛️ INSTITUTIONAL
                  <span className="ml-1 rounded-sm bg-[#a78bfa]/15 px-1.5 py-[1px] text-[9px] tracking-normal text-[#ddd6fe]">
                    {maxConsensus > 0 ? `${maxConsensus}v` : "–"}
                    {" · "}
                    {Math.round(bestConf * 100)}% conf
                  </span>
                  <div
                    className="invisible absolute left-0 right-auto top-full z-30 mt-2 w-[360px] max-w-[calc(100vw-2rem)] -translate-y-1 rounded-md border border-[#a78bfa]/30 bg-[#0a0a0c]/95 px-3 py-2.5 text-left opacity-0 shadow-[0_8px_24px_-12px_rgba(167,139,250,0.45)] backdrop-blur-md transition-all duration-150 group-hover/inst:visible group-hover/inst:translate-y-0 group-hover/inst:opacity-100 group-focus-within/inst:visible group-focus-within/inst:translate-y-0 group-focus-within/inst:opacity-100 sm:max-w-[360px]"
                    data-testid="liqmap-institutional-tooltip"
                  >
                    <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#a78bfa]/80">
                      Refinamientos institucionales (5)
                    </p>
                    <div className="mt-2 space-y-1.5 font-mono text-[10px] leading-relaxed text-white/85">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-white/55">Funding skew L/S</span>
                        <span className="font-semibold text-[#ddd6fe]">
                          {longsPct != null ? `${longsPct} / ${shortsPct}` : "–"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-white/55">Volatilidad 24h</span>
                        <span className="font-semibold text-[#ddd6fe]">
                          {vol != null ? `${(vol * 100).toFixed(2)}%` : "–"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-white/55">Consenso máx · ≥3v</span>
                        <span className="font-semibold text-[#ddd6fe]">
                          {maxConsensus}v · {buckets3plus} buckets
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-white/55">{hc.real_liq_validated}</span>
                        <span className="font-semibold text-[#ddd6fe]">
                          {realLiqs.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-white/55">Confidence max / sup</span>
                        <span className="font-semibold text-[#ddd6fe]">
                          {Math.round(cmaxConf * 100)}% / {Math.round(csupConf * 100)}%
                        </span>
                      </div>
                    </div>
                    <p className="mt-2 border-t border-white/5 pt-1.5 font-mono text-[9px] leading-snug text-white/45">
                      <span className="text-white/65">Confidence</span> = % del cluster validado por liquidaciones reales ejecutadas en ±2.5% de esa zona.
                      <br />
                      {bestConf >= 0.5
                        ? "Cluster validado por liquidaciones reales recientes."
                        : "Cluster proyectado — aún sin liqs reales en esa zona."}
                    </p>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Stats */}
        <div className="mb-4">
          <StatsBar
            {...statsForBar}
            divergence={divergence}
            whaleVolume={showWhales && whaleData ? (whaleData.total_whale_volume_usd || 0) : 0}
            onClusterMaxClick={() => {
              if (cluster.maxPrice != null) setHighlight({ price: cluster.maxPrice, ts: Date.now() });
            }}
          />
        </div>

        {/* ─── RE₿EL LiqScore™ widget (added 2026-02-16) ─── */}
        <LiqScoreWidget
          coin={coin}
          onSimulateClick={() => {
            setSimOpen((v) => !v);
            if (!simEntryInput && statsForBar.price) {
              setSimEntryInput(String(Number(statsForBar.price.toFixed(2))));
            }
          }}
          onShareToShadow={onShareToShadow}
        />

        {/* ─── Cascade Predictor Banner — AI-powered, score>20 OR sticky 5min ─── */}
        <style>{`
          @keyframes rebelCascadePulseSlow { 0%,100% { opacity: 1; } 50% { opacity: 0.65; } }
          @keyframes rebelCascadePulseFast { 0%,100% { opacity: 1; box-shadow: 0 0 20px rgba(220,10,10,0.20);} 50% { opacity: 0.78; box-shadow: 0 0 32px rgba(220,10,10,0.45);} }
          @keyframes rebelCriticalBlink { 0%,100% { background: rgba(220,10,10,0.18); } 50% { background: rgba(220,10,10,0.38); } }
        `}</style>
        {(() => {
          // FIX 1 / CAUSE A+D — display when EITHER current score crosses
          // threshold OR sticky window (5 min) is still active. Closes when
          // user explicitly clicks the X (`bannerClosed`). `stickyOk` is
          // derived inside useCascadePredictor.
          const shouldShow = (cascadeScore > CASCADE_SHOW_THRESHOLD || stickyOk) && !bannerClosed;
          if (!shouldShow) return null;
          const lvl = cascadeData?.nivel
            || (cascadeScore >= 76 ? "INMINENTE" : cascadeScore >= 56 ? "ALTO" : "MODERADO");
          const tipo = cascadeData?.tipo || cascadeTipo(statsForBar.price || 0, cluster.maxPrice || 0);
          const bg = cascadeScore >= 76 ? "rgba(220,10,10,0.12)" : cascadeScore >= 56 ? "rgba(255,90,0,0.10)" : "rgba(240,200,0,0.08)";
          const bd = cascadeScore >= 76 ? "rgba(220,10,10,0.6)" : cascadeScore >= 56 ? "rgba(255,90,0,0.4)" : "rgba(240,200,0,0.3)";
          const fg = cascadeScore >= 76 ? "#ff3b5c" : cascadeScore >= 56 ? "#ff5a00" : "#f0c800";
          const anim = cascadeScore >= 76 ? "rebelCascadePulseFast 0.8s infinite" : cascadeScore >= 56 ? "rebelCascadePulseSlow 2s infinite" : "none";
          const shadow = cascadeScore >= 76 ? "0 0 20px rgba(220,10,10,0.2)" : "none";
          const precio = statsForBar.price || 0;
          // Cluster value is read from the single source of truth so it is
          // identical to the header stat, the canvas MAX label and the
          // arrow overlay — see useClusterData.
          const clusterMax = cluster.maxPrice || 0;
          const dist$ = Math.abs(precio - clusterMax);
          const distPct = clusterMax ? (dist$ / precio) * 100 : 0;
          const tsAt = cascadeData?.timestamp ? cascadeData.timestamp * 1000 : lastCallAt;
          const ago = tsAt ? Math.max(0, Math.floor((now - tsAt) / 1000)) : 0;
          const icon = cascadeScore >= 76 ? "🔴" : cascadeScore >= 56 ? "🟠" : "🟡";
          const tipoBg = tipo === "LONG CASCADE" ? "#ff3b5c" : "#00d084";
          // HALLAZGO 2 fix — unify narrative with the countdown panel.
          // If the cascade-timer reports the price is moving AWAY from the
          // cluster (i.e. tendencia opposite to the cascade type), demote
          // the TIPO badge from "active" red/green to a muted "latente"
          // grey. Both panels then read coherently as a single story.
          const trend = cascadeTimer?.tendencia;
          const tipoLatente = (
            (tipo === "LONG CASCADE" && trend === "alcista") ||
            (tipo === "SHORT CASCADE" && trend === "bajista")
          );
          const tipoBgFinal = tipoLatente ? "rgba(255,255,255,0.10)" : tipoBg;
          const tipoFgFinal = tipoLatente ? "rgba(255,255,255,0.55)" : "white";
          const tipoLabel = tipoLatente ? `${tipo} • LATENTE` : tipo;
          return (
            <div
              data-testid="cascade-banner"
              data-cascade-level={lvl}
              role="alert"
              className="relative mb-4 rounded-lg"
              style={{
                background: bg,
                border: `1px solid ${bd}`,
                borderRadius: 8,
                padding: "8px 14px",
                animation: anim,
                boxShadow: shadow,
              }}
            >
              <button
                type="button"
                onClick={() => setBannerClosed(true)}
                aria-label={t.cascade?.banner_close_aria || "Cerrar banner"}
                data-testid="cascade-banner-close"
                className="absolute right-2 top-2 inline-flex h-5 w-5 items-center justify-center rounded text-[#444] hover:text-white"
              >
                <XIcon size={12} />
              </button>

              {/* Fila 1 — header (PREDICTOR IA section) */}
              <div className="flex flex-wrap items-center gap-2 pr-6 font-mono" style={{ color: fg }}>
                <span className="text-[12px]">{icon}</span>
                <span className="text-[11px] font-bold tracking-wider">
                  {lvl === "INMINENTE"
                    ? (t.cascade?.risk_imminent || "CASCADA INMINENTE")
                    : lvl === "ALTO"
                      ? (t.cascade?.risk_high || "RIESGO ALTO")
                      : lvl === "MODERADO"
                        ? (t.cascade?.risk_moderate || "RIESGO MODERADO")
                        : (t.cascade?.risk_low || "RIESGO BAJO")}
                </span>
                <span className="text-[10px] opacity-80">· {coin}/USDT</span>
                <span
                  className="inline-flex items-center gap-1 rounded px-1.5 py-[1px] text-[8px] font-bold uppercase tracking-[0.18em]"
                  style={{ background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.7)" }}
                  data-testid="cascade-banner-source-label"
                >
                  {(tLm(lang).cascade?.source_label || "fuente")}: {(tLm(lang).cascade?.predictor_ia_label || "Predictor IA")}
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        aria-label={tLm(lang).cascade?.info_aria || "Cómo se calcula esta fuente"}
                        data-testid="cascade-banner-source-info"
                        className="ml-0.5 inline-flex h-3 w-3 items-center justify-center rounded-full text-white/55 hover:text-white focus:outline-none"
                      >
                        <Info size={10} aria-hidden="true" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      side="top"
                      align="start"
                      sideOffset={6}
                      className="max-w-[280px] border-white/10 bg-[#0d0a0d] px-3 py-2.5 text-[10px] leading-snug text-white/80 shadow-xl"
                      data-testid="cascade-banner-source-popover"
                    >
                      {tLm(lang).cascade?.predictor_ia_info}
                    </PopoverContent>
                  </Popover>
                </span>
                <span
                  className="text-[10px] opacity-80"
                  data-testid="cascade-predictor-score"
                  title="Score del Predictor IA (4 factores: distancia, intensidad, ratio liqs, cambio 24h). Distinto del REBEL LIQSCORE™ del header (6 factores institucionales incluyendo DeFi y ballenas)."
                >· Score Predictor: {cascadeScore}/100</span>
                <span
                  className="ml-1 inline-block rounded px-2 py-[1px] text-[8.5px] font-bold uppercase tracking-wider"
                  style={{ background: tipoBgFinal, color: tipoFgFinal }}
                  data-testid="cascade-tipo-badge"
                  data-cascade-latente={tipoLatente ? "true" : "false"}
                  title={tipoLatente
                    ? `${tipo} estructural: el cluster existe pero el precio se aleja (tendencia ${trend}). Riesgo latente, no activo.`
                    : `${tipo} activa: el cluster está en la dirección del movimiento actual del precio.`}
                >
                  {tipoLabel}
                </span>
              </div>

              {/* Fila 2 — distancia · 2026-02 FASE 4 single-source-of-truth:
                  precio/cluster/distancia provienen de la derivación canónica
                  (statsForBar.price para precio live y `cluster.maxPrice` vía
                  `useClusterData` para el cluster). El antiguo
                  `cascadeData._evaluated_at.cluster_max` (pinned 5 min) fue
                  eliminado del hook useCascadePredictor — era código muerto
                  para el render desde iteration_40. */}
              {(() => {
                const dispPrecio = precio;
                const dispCluster = clusterMax;
                const dispDistUsd = dist$;
                const dispDistPct = distPct;
                return (
                  <div className="mt-1 font-mono text-[10px]" style={{ color: "#aaa" }} data-testid="cascade-structured-row">
                    {t.cascade?.label_price || "Precio"} ${dispPrecio.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    {" → "}{t.cascade?.label_cluster || "Cluster"} ${dispCluster.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    {" · "}{t.cascade?.label_distance || "Distancia"}: ${dispDistUsd.toLocaleString(undefined, { maximumFractionDigits: 0 })} ({dispDistPct.toFixed(2)}%)
                  </div>
                );
              })()}

              {/* Fila 3 — análisis IA */}
              <div className="mt-1.5 flex items-start gap-1.5 italic" style={{ color: "#ccc", fontSize: 11 }} data-testid="cascade-analysis">
                {cascadeLoading && !cascadeData ? (
                  <>
                    <Loader2 className="mt-[2px] h-3 w-3 animate-spin" />
                    <span className="opacity-80">{t.cascade?.analyzing_ai || "Analizando con IA RE₿EL..."}</span>
                  </>
                ) : cascadeData?.analisis ? (
                  <>
                    <Sparkles className="mt-[2px] h-3 w-3 shrink-0" style={{ color: fg }} />
                    <span>
                      <span className="font-bold" style={{ color: fg, fontStyle: "normal" }}>IA RE₿EL:</span>{" "}
                      {cascadeData.analisis}
                    </span>
                  </>
                ) : (
                  /* Backend skips the LLM call when score≤30 to save tokens
                     (predictor.py:1087 returns analisis=null). The banner
                     still shows (cascadeScore>20 gate at line 1269), so we
                     fill the gap with an explicit i18n fallback. NO ✨
                     Sparkles icon on purpose — keeps the message visually
                     distinct from real Claude-generated copy. */
                  <span
                    className="opacity-70"
                    style={{ color: "#aaa" }}
                    data-testid="cascade-analysis-low-score"
                  >
                    {tLm(lang).cascade?.analysis_low_score}
                  </span>
                )}
              </div>

              {/* Fila 4 — footer */}
              <div className="mt-1.5 font-mono" style={{ color: "#444", fontSize: 8 }}>
                {(t.cascade?.updated_ago || "Actualizado hace {n}s").replace("{n}", ago)}
                {cascadeData?.cached ? ` · ${t.cascade?.cache_active || "Caché activo"}` : ""}
                {cascadeData?.fallback ? ` · ${t.cascade?.local_analysis || "análisis local"}` : ""}
                <button
                  type="button"
                  onClick={() => setCascadeRecentOpen(true)}
                  data-testid="cascade-history-btn"
                  className="ml-3 inline-flex items-center gap-1 rounded border border-white/10 px-1.5 py-[1px] text-[8px] uppercase tracking-wider text-white/60 hover:border-[#f97316]/40 hover:text-white"
                  title={t.cascade?.history_btn_title || "Abre el panel 'Predicciones Recientes' (verificadas por touch-then-bounce) filtrado al coin actual."}
                >
                  <ClipboardList size={9} />
                  {t.cascade?.history_btn || "Historial"}
                </button>
              </div>

              {/* ─── FEATURE 2 — Cascade Countdown (real-time) ──────────
                  Ninguna otra herramienta (CoinGlass, Kingfisher, HyBlock,
                  TradingView) predice CUÁNDO el precio llegará al cluster.
                  RE₿EL es la primera. */}
              {cascadeTimer && (() => {
                const ct = cascadeTimer;
                const tendencia = ct.tendencia;
                const cuentaActiva = ct.countdown_activo && ct.tiempo_estimado_min != null && (ct.probabilidad_pct || 0) > 15;
                if (!cuentaActiva && tendencia === "bajista") return null;  // not enough info yet
                const tLocal = cascadeTimerLocal;
                const prob = Math.max(0, Math.min(99, ct.probabilidad_pct || 0));
                const probColor = prob >= 70 ? "#ff3b5c" : prob >= 40 ? "#f97316" : "#f0c800";
                const ratio = Math.max(0.04, prob / 100);
                // Determine display state.
                let mode = "active";
                if (!cuentaActiva || tendencia === "alcista" || tendencia === "lateral") mode = "aliviada";
                else if (tLocal != null && tLocal <= 0) mode = "critica";
                // Format minutes → "Xh Ymin" or "Ymin".
                const fmtTimer = (mins) => {
                  if (mins == null || mins <= 0) return "0min";
                  const h = Math.floor(mins / 60);
                  const m = Math.floor(mins - h * 60);
                  const s = Math.floor((mins - h * 60 - m) * 60);
                  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}min`;
                  if (m > 0) return `${m}min ${String(s).padStart(2, "0")}s`;
                  return `${s}s`;
                };
                const validatedPct = (() => {
                  const cmax = snapshot?.stats?.cluster_max_confidence;
                  return typeof cmax === "number" ? Math.round(cmax * 100) : 0;
                })();
                const numVenues = snapshot?.stats?.consensus_max_exchanges || (snapshot?.sources?.length || 0);
                return (
                  <div
                    data-testid="cascade-countdown"
                    data-cascade-mode={mode}
                    className="mt-3 rounded-md"
                    style={{
                      padding: "10px 14px",
                      background: mode === "critica" ? "rgba(220,10,10,0.18)" : mode === "aliviada" ? "rgba(0,208,132,0.08)" : "rgba(249,115,22,0.08)",
                      border: `1px solid ${mode === "critica" ? "rgba(220,10,10,0.7)" : mode === "aliviada" ? "rgba(0,208,132,0.4)" : "rgba(249,115,22,0.3)"}`,
                      animation: mode === "critica" ? "rebelCriticalBlink 0.7s infinite" : "none",
                    }}
                  >
                    {/* Title row */}
                    <div className="flex items-center justify-between gap-2 font-mono" style={{ color: "#888", fontSize: 10, letterSpacing: 2 }}>
                      <span>{mode === "critica" ? (t.cascade?.zone_critical || "⚡ ZONA CRÍTICA") : (t.cascade?.estimated_cascade || "⏱️ CASCADA ESTIMADA")}</span>
                      <span
                        className="inline-flex items-center gap-1 rounded px-1.5 py-[1px] font-bold uppercase"
                        style={{
                          background: "rgba(255,255,255,0.05)",
                          color: "rgba(255,255,255,0.7)",
                          fontSize: 8,
                          letterSpacing: "0.18em",
                        }}
                        data-testid="cascade-countdown-source-label"
                      >
                        {(tLm(lang).cascade?.source_label || "fuente")}: {(tLm(lang).cascade?.cascade_timer_label || "Cascade Timer")}
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              aria-label={tLm(lang).cascade?.info_aria || "Cómo se calcula esta fuente"}
                              data-testid="cascade-countdown-source-info"
                              className="ml-0.5 inline-flex h-3 w-3 items-center justify-center rounded-full text-white/55 hover:text-white focus:outline-none"
                            >
                              <Info size={10} aria-hidden="true" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent
                            side="top"
                            align="end"
                            sideOffset={6}
                            className="max-w-[280px] border-white/10 bg-[#0d0a0d] px-3 py-2.5 text-[10px] leading-snug text-white/80 shadow-xl"
                            data-testid="cascade-countdown-source-popover"
                          >
                            {tLm(lang).cascade?.cascade_timer_info}
                          </PopoverContent>
                        </Popover>
                      </span>
                      <span style={{ color: "#666", letterSpacing: 1 }}>
                        {tendencia === "lateral"
                          ? (t.cascade?.trend_lateral || "LATERAL")
                          : tendencia === "alcista"
                            ? (t.cascade?.trend_bullish || "ALCISTA")
                            : tendencia === "bajista"
                              ? (t.cascade?.trend_bearish || "BAJISTA")
                              : tendencia.toUpperCase()}
                      </span>
                    </div>
                    {/* Timer / status */}
                    {mode === "aliviada" ? (
                      <div
                        className="mt-1.5 font-mono"
                        style={{ color: "#00d084", fontSize: 13, fontWeight: 700 }}
                        data-testid="cascade-countdown-aliviada"
                      >
                        {t.cascade?.pressure_relieved || "⏸ PRESIÓN ALIVIADA — precio alejándose del cluster"}
                      </div>
                    ) : mode === "critica" ? (
                      <div
                        className="mt-1.5 font-mono"
                        style={{ color: "#ff3b5c", fontSize: 22, fontWeight: 900 }}
                        data-testid="cascade-countdown-critica"
                      >
                        {t.cascade?.cluster_reached || "⚡ CLUSTER ALCANZADO — CASCADA INICIADA"}
                      </div>
                    ) : (
                      <div
                        className="mt-1.5 font-mono"
                        style={{ color: "#f97316", fontSize: 28, fontWeight: 900, lineHeight: 1.05 }}
                        data-testid="cascade-countdown-timer"
                      >
                        {fmtTimer(tLocal)}
                      </div>
                    )}
                    {/* Probability bar — only meaningful when active */}
                    {mode === "active" && (
                      <div className="mt-2">
                        <div className="flex items-center justify-between font-mono" style={{ color: "#888", fontSize: 9, letterSpacing: 1 }}>
                          <span>{t.cascade?.probability || "PROBABILIDAD"}</span>
                          <span style={{ color: probColor, fontWeight: 700 }} data-testid="cascade-countdown-prob">{prob}%</span>
                        </div>
                        <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full" style={{ background: "rgba(255,255,255,0.06)" }}>
                          <div
                            className="h-full rounded-full"
                            style={{ width: `${ratio * 100}%`, background: probColor, transition: "width 0.4s ease-out" }}
                          />
                        </div>
                      </div>
                    )}
                    {/* Validation footer */}
                    <div className="mt-2 font-mono" style={{ color: "#666", fontSize: 10 }}>
                      {(t.cascade?.validated_footer || "Validado: {venues} venues · {pct}% liqs reales")
                        .replace("{venues}", numVenues)
                        .replace("{pct}", validatedPct)}
                    </div>
                  </div>
                );
              })()}
            </div>
          );
        })()}

        {/* Chart container — wraps sticky layer toolbar + heatmap.
            Fullscreen targets this container so the toolbar stays visible. */}
        <div
          ref={chartContainerRef}
          className="relative"
          data-testid="liqmap-chart-container"
          data-liqmap-fs-container="1"
        >
          {/* Sticky layer toolbar — anchored to the chart container.
              Visible without scroll AND visible in fullscreen. */}
          <div
            className="sticky top-0 z-30 mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-white/5 bg-[#0a0a0c]/85 px-2 py-1.5 backdrop-blur-md"
            data-testid="liqmap-chart-toolbar"
          >
            {/* Whale layer toggle — overlays Layer 5 on the canvas. */}
            <button
              type="button"
              onClick={() => setShowWhales((v) => !v)}
              data-testid="liqmap-whales-toggle"
              aria-pressed={showWhales}
              title={t.toolbar?.whales_title || "Mostrar/ocultar ballenas (>$1M) y órdenes medianas ($100K+)"}
              className="rounded-md px-2.5 py-1 font-mono text-[10px] font-bold transition-all"
              style={showWhales
                ? { background: "rgba(14,165,233,0.15)", border: "1px solid rgba(14,165,233,0.5)", color: "#0ea5e9" }
                : { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.55)" }
              }
            >
              {whaleLoading ? "…" : (t.toolbar?.whales || "🐋 Ballenas")}
            </button>
            {/* S/R IA toggle — overlays Layer 6 on the canvas. */}
            <style>{`
              @keyframes rebelSrHotDot {
                0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(249,115,22,0.55); }
                50%      { transform: scale(1.25); box-shadow: 0 0 0 6px rgba(249,115,22,0); }
              }
            `}</style>
            <button
              type="button"
              onClick={toggleSoportesResistencias}
              data-testid="liqmap-sr-toggle"
              aria-pressed={showSR}
              title={srHighProb > 0
                ? (t.toolbar?.sr_setups_hot || "{n} setup{s} de alta probabilidad detectado{s}").replace("{n}", srHighProb).replace(/\{s\}/g, srHighProb > 1 ? "s" : "")
                : (t.toolbar?.sr_ai_title || "Soportes y resistencias detectados con IA")}
              disabled={srLoading}
              className="relative rounded-md px-2.5 py-1 font-mono text-[10px] font-bold transition-all"
              style={showSR
                ? { background: "rgba(59,130,246,0.18)", border: "1px solid rgba(59,130,246,0.6)", color: "#3b82f6" }
                : { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.55)" }
              }
            >
              {srLoading ? "…" : (t.toolbar?.sr_ai || "📐 S/R IA")}
              {srHighProb > 0 && (
                <span
                  data-testid="liqmap-sr-hot-badge"
                  className="absolute -right-1.5 -top-1.5 flex h-4 items-center gap-[1px] rounded-full px-1 font-mono text-[8px] font-bold text-white"
                  style={{
                    background: "#f97316",
                    border: "1.5px solid #0a0a0c",
                    boxShadow: "0 0 0 1px rgba(249,115,22,0.4)",
                    animation: "rebelSrHotDot 1.4s infinite",
                  }}
                  aria-label={`${srHighProb} setup${srHighProb > 1 ? "s" : ""} de alta probabilidad detectado${srHighProb > 1 ? "s" : ""} — click para ver`}
                >
                  <span style={{ fontSize: 7, lineHeight: 1 }}>🎯</span>
                  <span style={{ lineHeight: 1 }}>{srHighProb}</span>
                </span>
              )}
            </button>
            {/* "Rango de Negociación" toggle — simple high/low channel
                 over the displayed candles (reactive to brush sub-range).
                 Cyan dashed lines, no tier, no history. Independent from
                 S/R IA: both can be active simultaneously. */}
            <button
              type="button"
              onClick={() => setShowRange((v) => !v)}
              data-testid="liqmap-range-toggle"
              aria-pressed={showRange}
              title={t.toolbar?.range_title || "Rango de negociación: máximo y mínimo de las velas visibles (se ajusta al brush)"}
              className="rounded-md px-2.5 py-1 font-mono text-[10px] font-bold transition-all"
              style={showRange
                ? { background: "rgba(99,102,241,0.16)", border: "1px solid rgba(99,102,241,0.55)", color: "#6366f1" }
                : { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.55)" }
              }
            >
              {t.toolbar?.range || "↕ Rango"}
            </button>
            {/* "Tendencia" toggle — diagonal trendline (violet) detecting
                 swing pivots and projecting the line forward. Renders
                 broken portion with dimmed dotted style. */}
            <button
              type="button"
              onClick={() => setShowTrendline((v) => !v)}
              data-testid="liqmap-trendline-toggle"
              aria-pressed={showTrendline}
              title={t.toolbar?.trendline_title || "Línea de tendencia: une los pivotes recientes y la proyecta"}
              className="rounded-md px-2.5 py-1 font-mono text-[10px] font-bold transition-all"
              style={showTrendline
                ? { background: "rgba(139,92,246,0.18)", border: "1px solid rgba(139,92,246,0.6)", color: "#8b5cf6" }
                : { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.55)" }
              }
            >
              {t.toolbar?.trendline || "📈 Tendencia"}
            </button>
            {/* "Patrones Chartistas" toggle — Doble Techo / Doble Suelo,
                Triángulos y HCH. Magenta family to stay distinct from S/R
                (red/green), Rango (cyan), Tendencia (violet). 100%
                geometric; see chartPatterns.js. */}
            <button
              type="button"
              onClick={() => setShowPatterns((v) => !v)}
              data-testid="liqmap-patterns-toggle"
              aria-pressed={showPatterns}
              title={t.toolbar?.patterns_title || "Patrones chartistas: Doble Techo/Suelo, Triángulos y HCH (calibración per-coin)"}
              className="rounded-md px-2.5 py-1 font-mono text-[10px] font-bold transition-all"
              style={showPatterns
                ? { background: "rgba(168,85,247,0.18)", border: "1px solid rgba(168,85,247,0.6)", color: "#a855f7" }
                : { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.55)" }
              }
            >
              {t.toolbar?.patterns || "◆ Patrones"}
            </button>
            {/* "Velas" toggle — candle patterns (Hammer/Doji/Engulfing).
                Independent layer. Uses the green/red/gray semaphore in
                canvas to signal bullish/bearish/neutral context. */}
            <button
              type="button"
              onClick={() => setShowCandles((v) => !v)}
              data-testid="liqmap-candles-toggle"
              aria-pressed={showCandles}
              title={t.toolbar?.candles_title || "Patrones de vela: Hammer, Hanging Man, Inv. Hammer, Shooting Star, Doji, Engulfing"}
              className="rounded-md px-2.5 py-1 font-mono text-[10px] font-bold transition-all"
              style={showCandles
                ? { background: "rgba(236,72,153,0.16)", border: "1px solid rgba(236,72,153,0.55)", color: "#ec4899" }
                : { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.55)" }
              }
            >
              {t.toolbar?.candles || "🕯 Velas"}
            </button>
            {/* "EMA" toggle — 4 EMA lines (9/21/50/200) + 3 cross
                detectors (golden/death cross, 21×50, 9×21). Violet
                family to stay distinct from S/R (orange), Patterns
                (magenta), Candles (green), Whales (gold). */}
            <button
              type="button"
              onClick={() => setShowEMA((v) => !v)}
              data-testid="liqmap-ema-toggle"
              aria-pressed={showEMA}
              title={t.toolbar?.ema_title || "EMAs 9/21/50/200 con detección de cruces (Golden/Death, 21×50, 9×21). Relevancia depende del timeframe."}
              className="rounded-md px-2.5 py-1 font-mono text-[10px] font-bold transition-all"
              style={showEMA
                ? { background: "rgba(139,92,246,0.16)", border: "1px solid rgba(139,92,246,0.55)", color: "#8b5cf6" }
                : { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.55)" }
              }
            >
              {t.toolbar?.ema || "📈 EMA"}
            </button>
            {/* "Estocástico" toggle — Slow Stochastic Oscillator (K=14, D=3,
                Smooth=3). Reveals a subpanel below the main chart and marks
                cross signals inside overbought/oversold zones as dots on the
                heatmap. Amber family to signal "momentum indicator" — same
                color as Wyckoff Structure badge (both are context/momentum
                readings, not price overlays). */}
            <button
              type="button"
              onClick={() => setShowStochastic((v) => !v)}
              data-testid="liqmap-stochastic-toggle"
              aria-pressed={showStochastic}
              title={t.toolbar?.stochastic_title || "Oscilador Estocástico (K=14, D=3, Smooth=3)"}
              className="rounded-md px-2.5 py-1 font-mono text-[10px] font-bold transition-all"
              style={showStochastic
                ? { background: "rgba(245,158,11,0.16)", border: "1px solid rgba(245,158,11,0.55)", color: "#f59e0b" }
                : { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.55)" }
              }
            >
              {t.toolbar?.stochastic || "📊 Estocástico"}
            </button>
            {/* Wyckoff Structure toggle — opens the lateral drawer. NOT a
                canvas layer: it's a macro-context drawer. Amber to signal
                its role as "market structure summary", distinct from the
                canvas overlay toggles above. Border shifts to gold when
                the consolidation crosses 7d ("extended") or 14d ("major",
                also pulses) — so the toolbar hints "something big is
                happening in Structure" without opening the drawer. */}
            {(() => {
              const b = structureBadge || {};
              const durationHours = b.duration_hours || 0;
              const tier = !b.is_ranging
                ? null
                : durationHours >= 336 ? "major"
                : durationHours >= 168 ? "extended"
                : "standard";
              const style = !b.is_ranging
                ? { background: "rgba(245,158,11,0.14)", border: "1px solid rgba(245,158,11,0.55)", color: "#f59e0b" }
                : tier === "major"
                  ? { background: "rgba(234,179,8,0.20)", border: "1px solid rgba(234,179,8,0.85)", color: "#eab308" }
                  : tier === "extended"
                    ? { background: "rgba(234,179,8,0.14)", border: "1px solid rgba(234,179,8,0.60)", color: "#eab308" }
                    : { background: "rgba(0,208,132,0.14)", border: "1px solid rgba(0,208,132,0.55)", color: "#00d084" };
              return (
                <button
                  type="button"
                  onClick={() => setStructureOpen(true)}
                  data-testid="liqmap-structure-badge"
                  data-is-ranging={String(!!b.is_ranging)}
                  data-tier={tier || "none"}
                  title={tLm(lang).wyckoff?.open_btn_title || "Ver estructura de mercado"}
                  className={"relative rounded-md px-2.5 py-1 font-mono text-[10px] font-bold transition-all " + (tier === "major" ? "animate-pulse" : "")}
                  style={style}
                >
                  📐 {b.is_ranging
                    ? ((tLm(lang).wyckoff?.badge_ranging || "RANGO ACTIVO · {tf}").replace("📐 ", "").replace("{tf}", b.tf || "1h")
                        + (durationHours
                            ? (" · " + (durationHours >= 24
                                ? `${(durationHours / 24).toFixed(1)}d`
                                : `${Math.round(durationHours)}h`))
                            : ""))
                    : (t.toolbar?.structure_default || "Estructura")}
                  {b.has_recent_event && (
                    <span
                      data-testid="liqmap-structure-event-flag"
                      className="absolute -right-1.5 -top-1.5 flex h-4 items-center justify-center rounded-full px-1 font-mono text-[8px] font-bold text-white"
                      style={{ background: "#f97316", border: "1.5px solid #0a0a0c" }}
                      aria-label={`${b.events_count} eventos detectados`}
                    >
                      {b.events_count}
                    </span>
                  )}
                </button>
              );
            })()}
            {/* Setups history modal opener — discoverable shortcut to credibility data. */}
            <button
              type="button"
              onClick={openSrRecent}
              data-testid="liqmap-sr-recent-btn"
              title={t.toolbar?.historical_title || "Histórico de setups detectados y su % real de acierto"}
              className="rounded-md px-2.5 py-1 font-mono text-[10px] font-bold transition-all"
              style={{
                background: "rgba(20,184,166,0.10)",
                border: "1px solid rgba(20,184,166,0.4)",
                color: "#14b8a6",
              }}
            >
              {t.toolbar?.historical || "📜 Histórico"}
            </button>
            {/* Cascade Predictor "Predicciones Recientes" opener — parallel
                credibility panel for the AI Cascade Predictor (separate from
                the S/R panel above). */}
            <button
              type="button"
              onClick={() => setCascadeRecentOpen(true)}
              data-testid="liqmap-cascade-recent-btn"
              title={t.toolbar?.predictions_title || "Histórico de predicciones del Cascade Predictor IA y su % real de acierto"}
              className="rounded-md px-2.5 py-1 font-mono text-[10px] font-bold transition-all"
              style={{
                background: "rgba(249,115,22,0.10)",
                border: "1px solid rgba(249,115,22,0.4)",
                color: "#f97316",
              }}
            >
              {t.toolbar?.predictions || "📊 Predicciones"}
            </button>
            {/* Cascade Timer histórico — Plan A v2 P1. Suprime stats hasta
                 alcanzar TIMER_MIN_FOR_STATS=30 evaluadas/coin (Decisión #4). */}
            <button
              type="button"
              onClick={() => setCascadeTimerHistoryOpen(true)}
              data-testid="liqmap-cascade-timer-history-btn"
              title={t.toolbar?.timer_title || "Histórico del Cascade Timer (countdown vs tiempo real)"}
              className="rounded-md px-2.5 py-1 font-mono text-[10px] font-bold transition-all"
              style={{
                background: "rgba(239,68,68,0.10)",
                border: "1px solid rgba(239,68,68,0.4)",
                color: "#ef4444",
              }}
            >
              {t.toolbar?.timer || "⏱️ Timer"}
            </button>
            {/* Cross-coin Top S/R levels — ranks the strongest S/R across
                 the 6 coins using the same composite strength_score the
                 Heatmap shows. Read-only: no recalculation, no writes. */}
            <button
              type="button"
              onClick={() => setSrTopLevelsOpen(true)}
              data-testid="liqmap-sr-top-levels-btn"
              title={tLm(lang).sr_top?.btn_title || "Top niveles S/R cross-coin"}
              className="rounded-md px-2.5 py-1 font-mono text-[10px] font-bold transition-all"
              style={{
                background: "rgba(234,179,8,0.10)",
                border: "1px solid rgba(234,179,8,0.4)",
                color: "#eab308",
              }}
            >
              {tLm(lang).sr_top?.btn_label || "🏆 Top S/R"}
            </button>
            {/* Manual drawing tools — opens the floating drawing toolbar.
                Magenta family kept for chartist patterns; pure cyan-on-white
                glyph here so it stays distinct on the dense filter row. */}
            <button
              type="button"
              onClick={() => setShowDrawingTool((v) => !v)}
              data-testid="liqmap-drawing-toggle"
              aria-pressed={showDrawingTool}
              title={tLm(lang).patterns?.drawing?.toggle_title || "Herramientas de dibujo manual"}
              className="rounded-md px-2.5 py-1 font-mono text-[10px] font-bold transition-all"
              style={showDrawingTool
                ? { background: "rgba(113,113,122,0.20)", border: "1px solid rgba(113,113,122,0.6)", color: "#a1a1aa" }
                : { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.55)" }
              }
            >
              ✎ {tLm(lang).patterns?.drawing?.toggle_label || "Dibujar"}
            </button>
          </div>
          <div
            className="relative h-[58vh] min-h-[420px] rounded-2xl border bg-[#02000c]"
            style={{ borderColor: "rgba(249,115,22,0.2)", filter: trialExpired ? "blur(6px)" : "none" }}
            data-testid="liqmap-canvas-wrap"
          >
            <HeatmapCanvas
              clusters={snapshot?.clusters || []}
              candles={displayedCandles}
              livePrice={livePrice}
              stats={snapshot?.stats || {}}
              highlightPrice={highlight ? `${highlight.price}|${highlight.ts}` : null}
              simulation={simResult ? { entryPrice: simResult.entry, liqPrice: simResult.liq, direction: simResult.direction } : null}
              whales={showWhales ? whaleData : null}
              srData={showSR ? srData : null}
              showRange={showRange}
              showTrendline={showTrendline}
              showPatterns={showPatterns}
              showCandles={showCandles}
              showEMA={showEMA}
              showStochastic={showStochastic}
              stochasticCrosses={stochasticCrosses}
              coin={coin}
              timeframe={timeframe}
              lang={lang}
              onResetView={() => setBrushRange(null)}
              fullscreenTargetRef={chartContainerRef}
              showDrawingTool={showDrawingTool}
              drawings={drawings}
              onDrawingsChange={commitDrawings}
              activeTool={activeTool}
              magnetOn={magnetOn}
              allDrawingsLocked={allDrawingsLocked}
              allDrawingsHidden={allDrawingsHidden}
              persistentMode={persistentMode}
              onAutoRevertTool={() => setActiveTool("cursor")}
            />
            {loading && !snapshot && (
              <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center text-xs text-white/55">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t.loading}
              </div>
            )}
            {err && (
              <div className="pointer-events-none absolute left-3 top-3 z-10 rounded-md border border-amber-400/30 bg-amber-400/[0.07] px-3 py-1.5 text-[11px] text-amber-200" data-testid="liqmap-err">
                {err}
              </div>
            )}

            {/* ─── Stale candles indicator ─────────────────────────────
                Surfaces when the backend served candles from
                last-known-good cache because OKX + Gate.io both failed
                the upstream refresh. Discreet but visible: small amber
                pill, top-right of the chart, never blocks pointer
                events. Disappears automatically when upstream recovers
                (the next fetch tick clears `candlesStale`). */}
            {candlesStale && (
              <div
                className="pointer-events-none absolute right-3 top-3 z-10 flex items-center gap-1.5 rounded-md border border-amber-400/40 bg-amber-400/[0.10] px-2.5 py-1 font-mono text-[10px] font-bold text-amber-200"
                data-testid="liqmap-candles-stale"
                title={`Datos de candles servidos desde caché (OKX/Gate.io momentáneamente sin respuesta). Edad: ${candlesStale.age_s}s.`}
              >
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-300" />
                Datos de hace {candlesStale.age_s}s
              </div>
            )}
            {/* ─── Stale snapshot indicator ─────────────────────────────
                Surfaces when GET /api/liqmap/{coin} fell back to a
                lookback snapshot (5-min window) because the latest tick
                was rejected (insufficient exchanges OK, or
                validate_cluster_max rejection). Independent from the
                candles badge — different upstream, different outage. */}
            {snapshotStale && (
              <div
                className="pointer-events-none absolute right-3 top-12 z-10 flex items-center gap-1.5 rounded-md border border-orange-400/40 bg-orange-400/[0.10] px-2.5 py-1 font-mono text-[10px] font-bold text-orange-200"
                data-testid="liqmap-snapshot-stale"
                title={`Snapshot servido desde lookback de hasta 5 minutos (último tick rechazado o vacío). Edad: ${snapshotStale.age_s}s.`}
              >
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-orange-300" />
                LiqScore de hace {snapshotStale.age_s}s
              </div>
            )}

            {/* ─── Manual drawing toolbar (Fase B1) ─────────────────
                Floating left-edge panel, z-15 (above candle-legend z-10,
                below sticky filter toolbar z-30). Visible only when the
                user enabled drawing via the filter-row "✎ Dibujar" btn. */}
            {showDrawingTool && (() => {
              const tD = tLm(lang).patterns?.drawing || {};
              // Always-visible: cursor (top) — exits any tool back to pan/zoom.
              const cursorBtn = { id: DRAW_TOOLS.CURSOR, icon: "↖", label: tD.tool_cursor || "Cursor (pan/zoom)" };
              // 6 categorías agrupadas. Cada item conserva su data-testid
              // `liqmap-drawing-tool-${id}` para no romper tests existentes.
              // Los 12 tools de B5a (highlighter, pitchfork, fib_channel,
              // fib_extension_trend, forecast, price_range, date_range,
              // date_price_range, abcd, head_shoulders, triangle_pattern,
              // cycle_lines) entran directo en su grupo — no pasan por un
              // array plano intermedio.
              const groups = [
                {
                  id: "lineas",
                  icon: "╱",
                  label: tD.group_lineas || "Líneas",
                  items: [
                    { id: DRAW_TOOLS.LINE,         icon: "╱",  label: tD.tool_line       || "Línea de tendencia" },
                    { id: DRAW_TOOLS.RAY,          icon: "→",  label: tD.tool_ray        || "Ray (línea infinita en una dirección)" },
                    { id: DRAW_TOOLS.HORIZONTAL,   icon: "─",  label: tD.tool_horizontal || "Línea horizontal" },
                    { id: DRAW_TOOLS.HORIZONTAL_RAY, icon: "→─", label: tD.tool_horizontal_ray || "Horizontal ray (solo hacia la derecha)" },
                    { id: DRAW_TOOLS.VERTICAL,     icon: "│",  label: tD.tool_vertical   || "Línea vertical" },
                    { id: DRAW_TOOLS.VERTICAL_RAY, icon: "↑│", label: tD.tool_vertical_ray || "Vertical ray (solo hacia arriba)" },
                    { id: DRAW_TOOLS.CHANNEL,      icon: "∥",  label: tD.tool_channel    || "Canal paralelo (3 clicks)" },
                    { id: DRAW_TOOLS.PITCHFORK,    icon: "Ψ",  label: tD.tool_pitchfork  || "Pitchfork de Andrews (3 clicks)" },
                    { id: DRAW_TOOLS.SCHIFF_PITCHFORK, icon: "Ψs", label: tD.tool_schiff_pitchfork || "Schiff Pitchfork (3 clicks)" },
                    { id: DRAW_TOOLS.MODIFIED_SCHIFF_PITCHFORK, icon: "Ψm", label: tD.tool_modified_schiff_pitchfork || "Modified Schiff Pitchfork (3 clicks)" },
                  ],
                },
                {
                  id: "fib",
                  icon: "ƒ",
                  label: tD.group_fib || "Fibonacci y Gann",
                  items: [
                    { id: DRAW_TOOLS.FIB_RETRACEMENT,     icon: "ƒ",  label: tD.tool_fib_ret        || "Fibonacci Retracement" },
                    { id: DRAW_TOOLS.FIB_EXTENSION,       icon: "ƒ⁺", label: tD.tool_fib_ext        || "Fibonacci Extension" },
                    { id: DRAW_TOOLS.FIB_TIMEZONE,        icon: "ƒt", label: tD.tool_fib_timezone   || "Fib Time Zone" },
                    { id: DRAW_TOOLS.FIB_SPEEDFAN,        icon: "⌇",  label: tD.tool_fib_speedfan   || "Fib Speed Resistance Fan" },
                    { id: DRAW_TOOLS.FIB_WEDGE,           icon: "⋀",  label: tD.tool_fib_wedge      || "Fib Wedge" },
                    { id: DRAW_TOOLS.GANN_FAN,            icon: "⩘",  label: tD.tool_gann_fan       || "Gann Fan" },
                    { id: DRAW_TOOLS.GANN_BOX,            icon: "⊞",  label: tD.tool_gann_box       || "Gann Box" },
                    { id: DRAW_TOOLS.FIB_CHANNEL,         icon: "ƒ∥", label: tD.tool_fib_channel    || "Fib Channel" },
                    { id: DRAW_TOOLS.FIB_EXTENSION_TREND, icon: "ƒT", label: tD.tool_fib_ext_trend  || "Fib Extension por Tendencia (3 clicks)" },
                  ],
                },
                {
                  id: "patrones",
                  icon: "△",
                  label: tD.group_patrones || "Patrones",
                  items: [
                    { id: DRAW_TOOLS.XABCD,              icon: "X̲", label: tD.tool_xabcd            || "XABCD (5 clicks)" },
                    { id: DRAW_TOOLS.CYPHER,             icon: "Cy", label: tD.tool_cypher           || "Cypher (5 clicks)" },
                    { id: DRAW_TOOLS.THREE_DRIVES,       icon: "3D", label: tD.tool_three_drives     || "Three Drives (3 clicks)" },
                    { id: DRAW_TOOLS.ELLIOTT_IMPULSE,    icon: "EI", label: tD.tool_elliott_imp      || "Elliott Impulso (5 clicks)" },
                    { id: DRAW_TOOLS.ELLIOTT_CORRECTION, icon: "Ec", label: tD.tool_elliott_corr     || "Elliott Corrección (3 clicks)" },
                    { id: DRAW_TOOLS.ABCD,               icon: "AB", label: tD.tool_abcd             || "ABCD manual (4 clicks)" },
                    { id: DRAW_TOOLS.HEAD_SHOULDERS,     icon: "HS", label: tD.tool_head_shoulders   || "Cabeza y Hombros (7 clicks)" },
                    { id: DRAW_TOOLS.TRIANGLE_PATTERN,   icon: "▲▽", label: tD.tool_triangle_pattern || "Triángulo (chartist, 4 clicks)" },
                    { id: DRAW_TOOLS.FLAG_CHARTIST,      icon: "⚑",  label: tD.tool_flag_chartist    || "Flag / Pennant / Wedge chartist (4 clicks)" },
                    { id: DRAW_TOOLS.CUP_HANDLE,         icon: "⌣",  label: tD.tool_cup_handle       || "Cup & Handle (5 clicks)" },
                  ],
                },
                {
                  id: "prediccion",
                  icon: "📏",
                  label: tD.group_prediccion || "Predicción y medición",
                  items: [
                    { id: DRAW_TOOLS.MEASURE,          icon: "📏", label: tD.tool_measure          || "Medidor (transitorio)" },
                    { id: DRAW_TOOLS.POSITION,         icon: "⚖",  label: tD.tool_position         || "Posición Long/Short (3 clicks)" },
                    { id: DRAW_TOOLS.FORECAST,         icon: "⇢",  label: tD.tool_forecast         || "Forecast / Proyección" },
                    { id: DRAW_TOOLS.PRICE_RANGE,      icon: "⇕",  label: tD.tool_price_range      || "Medir rango (precio)" },
                    { id: DRAW_TOOLS.DATE_RANGE,       icon: "⇔",  label: tD.tool_date_range       || "Medir rango (fecha)" },
                    { id: DRAW_TOOLS.DATE_PRICE_RANGE, icon: "⤡",  label: tD.tool_date_price_range || "Medir rango (fecha + precio)" },
                    { id: DRAW_TOOLS.CYCLE_LINES,      icon: "⫶",  label: tD.tool_cycle_lines      || "Líneas cíclicas" },
                    { id: DRAW_TOOLS.BARS_PATTERN,     icon: "▦",  label: tD.tool_bars_pattern     || "Patrón de barras (capturar + reposicionar)" },
                  ],
                },
                {
                  id: "formas",
                  icon: "▭",
                  label: tD.group_formas || "Formas",
                  items: [
                    { id: DRAW_TOOLS.RECT,     icon: "▭",  label: tD.tool_rect     || "Rectángulo" },
                    { id: DRAW_TOOLS.CIRCLE,   icon: "○",  label: tD.tool_circle   || "Círculo" },
                    { id: DRAW_TOOLS.ELLIPSE,  icon: "◯",  label: tD.tool_ellipse  || "Elipse" },
                    { id: DRAW_TOOLS.TRIANGLE, icon: "△",  label: tD.tool_triangle || "Triángulo (3 clicks)" },
                    { id: DRAW_TOOLS.FREEHAND, icon: "✎",  label: tD.tool_freehand || "Pincel libre" },
                    { id: DRAW_TOOLS.POLYLINE, icon: "⋮⋮", label: tD.tool_polyline || "Polilínea (dbl-click/Enter)" },
                  ],
                },
                {
                  id: "anotacion",
                  icon: "T",
                  label: tD.group_anotacion || "Anotación",
                  items: [
                    { id: DRAW_TOOLS.TEXT,        icon: "T",  label: tD.tool_text        || "Texto" },
                    { id: DRAW_TOOLS.CALLOUT,     icon: "💬", label: tD.tool_callout     || "Callout (flecha + texto)" },
                    { id: DRAW_TOOLS.FLAG,        icon: "🚩", label: tD.tool_flag        || "Flag / Signpost" },
                    { id: DRAW_TOOLS.HIGHLIGHTER, icon: "▰",  label: tD.tool_highlighter || "Resaltador" },
                  ],
                },
              ];
              const onPickTool = (id) => setActiveTool((cur) => (cur === id ? "cursor" : id));
              // A group trigger is "active" if any of its items is the
              // current active tool — provides instant visual feedback.
              const groupIsActive = (g) => g.items.some((it) => it.id === activeTool);
              return (
                <div
                  data-testid="liqmap-drawing-toolbar"
                  className="absolute top-3 left-3 flex flex-col gap-1 rounded-lg border border-cyan-400/30 bg-[#0a0a0c]/90 p-1 backdrop-blur-md"
                  style={{ zIndex: 15, pointerEvents: "auto" }}
                >
                  {/* Cursor — always visible at the top of the rail. */}
                  <button
                    key={cursorBtn.id}
                    type="button"
                    onClick={() => onPickTool(cursorBtn.id)}
                    data-testid={`liqmap-drawing-tool-${cursorBtn.id}`}
                    aria-pressed={activeTool === cursorBtn.id}
                    title={cursorBtn.label}
                    className="flex h-7 w-7 items-center justify-center rounded-md font-mono text-[12px] font-bold transition-colors"
                    style={activeTool === cursorBtn.id
                      ? { background: "rgba(34,211,238,0.18)", border: "1px solid rgba(34,211,238,0.65)", color: "#22d3ee" }
                      : { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.65)" }
                    }
                  >
                    {cursorBtn.icon}
                  </button>
                  {/* 6 category triggers — open a Popover with the items.
                      Each Popover is fully controlled via the shared
                      `openGroup` state so that:
                        • Selecting a tool inside a flyout closes that flyout
                          deterministically (no KeyboardEvent hacks).
                        • Opening a different group automatically closes the
                          previous one (mutual exclusivity).
                        • Mobile / touch behavior stays robust because we
                          never depend on focus to dismiss the menu. */}
                  {groups.map((g) => {
                    const active = groupIsActive(g);
                    const isOpen = openGroup === g.id;
                    return (
                      <Popover
                        key={g.id}
                        open={isOpen}
                        onOpenChange={(next) => setOpenGroup(next ? g.id : null)}
                      >
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            data-testid={`liqmap-drawing-group-${g.id}`}
                            aria-label={g.label}
                            title={g.label}
                            className="flex h-7 w-7 items-center justify-center rounded-md font-mono text-[12px] font-bold transition-colors"
                            style={active
                              ? { background: "rgba(34,211,238,0.18)", border: "1px solid rgba(34,211,238,0.65)", color: "#22d3ee" }
                              : { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.65)" }
                            }
                          >
                            {g.icon}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent
                          side="right"
                          align="start"
                          sideOffset={6}
                          className="w-auto max-w-[260px] border border-cyan-400/30 bg-[#0a0a0c]/95 p-2 backdrop-blur-md"
                          data-testid={`liqmap-drawing-group-${g.id}-content`}
                        >
                          <div className="mb-1 px-1 font-mono text-[10px] uppercase tracking-wider text-cyan-300/80">
                            {g.label}
                          </div>
                          <div className="grid grid-cols-4 gap-1">
                            {g.items.map((it) => {
                              const itActive = activeTool === it.id;
                              return (
                                <button
                                  key={it.id}
                                  type="button"
                                  onClick={() => {
                                    onPickTool(it.id);
                                    // Explicit close — do NOT rely on
                                    // onOpenChange firing from focus/outside
                                    // events. This guarantees the flyout
                                    // dismisses on every selection, on every
                                    // platform (touch + desktop).
                                    setOpenGroup(null);
                                  }}
                                  data-testid={`liqmap-drawing-tool-${it.id}`}
                                  aria-pressed={itActive}
                                  title={it.label}
                                  className="flex h-9 min-w-[36px] items-center justify-center rounded-md font-mono text-[12px] font-bold transition-colors"
                                  style={itActive
                                    ? { background: "rgba(34,211,238,0.20)", border: "1px solid rgba(34,211,238,0.75)", color: "#22d3ee" }
                                    : { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)", color: "rgba(229,231,235,0.85)" }
                                  }
                                >
                                  {it.icon}
                                </button>
                              );
                            })}
                          </div>
                        </PopoverContent>
                      </Popover>
                    );
                  })}
                  {/* Eraser — always visible right below the category triggers. */}
                  <button
                    key={DRAW_TOOLS.ERASER}
                    type="button"
                    onClick={() => onPickTool(DRAW_TOOLS.ERASER)}
                    data-testid={`liqmap-drawing-tool-${DRAW_TOOLS.ERASER}`}
                    aria-pressed={activeTool === DRAW_TOOLS.ERASER}
                    title={tD.tool_eraser || "Borrar"}
                    className="flex h-7 w-7 items-center justify-center rounded-md font-mono text-[12px] font-bold transition-colors"
                    style={activeTool === DRAW_TOOLS.ERASER
                      ? { background: "rgba(239,68,68,0.18)", border: "1px solid rgba(239,68,68,0.65)", color: "#ef4444" }
                      : { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.65)" }
                    }
                  >
                    ✕
                  </button>
                  {/* Separator */}
                  <div style={{ height: 1, background: "rgba(148,163,184,0.18)", margin: "2px 0" }} />
                  {/* Magnet modal toggle */}
                  <button
                    type="button"
                    onClick={() => setMagnetOn((v) => !v)}
                    data-testid="liqmap-drawing-magnet"
                    aria-pressed={magnetOn}
                    title={tD.tool_magnet || "Imán: ajustar a vela (OHLC)"}
                    className="flex h-7 w-7 items-center justify-center rounded-md font-mono text-[12px] font-bold transition-colors"
                    style={magnetOn
                      ? { background: "rgba(168,85,247,0.18)", border: "1px solid rgba(168,85,247,0.65)", color: "#a855f7" }
                      : { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.65)" }
                    }
                  >
                    ⌖
                  </button>
                  {/* Persistent mode (pin) — keep active tool after each trace */}
                  <button
                    type="button"
                    onClick={() => setPersistentMode((v) => !v)}
                    data-testid="liqmap-drawing-persistent"
                    aria-pressed={persistentMode}
                    title={tD.toggle_persistent || "Modo dibujo persistente (no revertir tras cada trazo)"}
                    className="flex h-7 w-7 items-center justify-center rounded-md font-mono text-[12px] font-bold transition-colors"
                    style={persistentMode
                      ? { background: "rgba(34,197,94,0.18)", border: "1px solid rgba(34,197,94,0.65)", color: "#22c55e" }
                      : { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.65)" }
                    }
                  >
                    📌
                  </button>
                  {/* Lock all — block eraser mutations on every drawing */}
                  <button
                    type="button"
                    onClick={() => setAllDrawingsLocked((v) => !v)}
                    data-testid="liqmap-drawing-lock-all"
                    aria-pressed={allDrawingsLocked}
                    title={tD.toggle_lock_all || "Bloquear todos los dibujos"}
                    className="flex h-7 w-7 items-center justify-center rounded-md font-mono text-[12px] font-bold transition-colors"
                    style={allDrawingsLocked
                      ? { background: "rgba(250,204,21,0.18)", border: "1px solid rgba(250,204,21,0.65)", color: "#facc15" }
                      : { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.65)" }
                    }
                  >
                    🔒
                  </button>
                  {/* Hide all — skip rendering all drawings */}
                  <button
                    type="button"
                    onClick={() => setAllDrawingsHidden((v) => !v)}
                    data-testid="liqmap-drawing-hide-all"
                    aria-pressed={allDrawingsHidden}
                    title={tD.toggle_hide_all || "Ocultar todos los dibujos"}
                    className="flex h-7 w-7 items-center justify-center rounded-md font-mono text-[12px] font-bold transition-colors"
                    style={allDrawingsHidden
                      ? { background: "rgba(148,163,184,0.18)", border: "1px solid rgba(148,163,184,0.65)", color: "#94a3b8" }
                      : { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.65)" }
                    }
                  >
                    👁
                  </button>
                  {/* Clear-all (only visible when there are drawings) */}
                  {drawings.length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        // eslint-disable-next-line no-alert
                        if (window.confirm(tD.confirm_clear_all || "¿Borrar todos los dibujos de esta vista?")) {
                          commitDrawings([]);
                          setActiveTool("cursor");
                        }
                      }}
                      data-testid="liqmap-drawing-clear-all"
                      title={tD.tool_clear_all || "Borrar todo"}
                      className="flex h-7 w-7 items-center justify-center rounded-md font-mono text-[10px] font-bold transition-colors"
                      style={{ background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.45)", color: "#ef4444" }}
                    >
                      🗑
                    </button>
                  )}
                  {/* Share permalink — copies a #draw=... URL with the current drawings */}
                  {drawings.length > 0 && (
                    <button
                      type="button"
                      onClick={onShareDrawings}
                      data-testid="liqmap-drawing-share"
                      title={tD.tool_share || "Compartir dibujos como enlace"}
                      className="flex h-7 w-7 items-center justify-center rounded-md font-mono text-[12px] font-bold transition-colors"
                      style={{ background: "rgba(34,211,238,0.10)", border: "1px solid rgba(34,211,238,0.45)", color: "#22d3ee" }}
                    >
                      🔗
                    </button>
                  )}
                </div>
              );
            })()}

            {/* Legend toggle button — same visual family as zoom controls */}
            <button
              ref={legendBtnRef}
              type="button"
              onClick={() => setLegendOpen((v) => !v)}
              aria-label="Toggle legend"
              data-testid="liqmap-legend-btn"
              className="absolute bottom-3 right-3 z-20 inline-flex h-7 w-7 items-center justify-center rounded-md border border-[#f97316]/30 bg-white/[0.05] font-mono text-xs font-bold text-[#f97316] transition-all hover:border-[#f97316] hover:bg-[#f97316]/10"
            >
              {tL.help}
            </button>

            {/* ─── Simulator toggle button MOVED to LiqScoreWidget row ───
                (see <LiqScoreWidget onSimulateClick={...}/> above; the canvas
                overlay version was removed 2026-06-17 per user request). */}

            {/* ─── Simulator panel (right side on desktop, bottom-sheet on mobile) ─── */}
            {simOpen && (() => {
              const presets = [2, 5, 10, 20, 50, 100, 125];
              const refPrice = statsForBar.price || 0;
              const longBg = simDirection === "LONG" ? "#00d084" : "rgba(255,255,255,0.04)";
              const shortBg = simDirection === "SHORT" ? "#ff3b5c" : "rgba(255,255,255,0.04)";
              const longFg = simDirection === "LONG" ? "#000" : "#aaa";
              const shortFg = simDirection === "SHORT" ? "#fff" : "#aaa";
              return (
                <div
                  data-testid="liqmap-sim-panel"
                  className="absolute z-30 flex flex-col gap-3 overflow-y-auto rounded-xl border p-4 backdrop-blur-md
                    sm:right-0 sm:top-0 sm:h-full sm:w-[220px] sm:max-w-[90%]
                    inset-x-0 bottom-0 max-h-[80vh] w-full sm:inset-x-auto sm:bottom-auto"
                  style={{
                    background: "rgba(8,5,8,0.96)",
                    borderColor: "rgba(249,115,22,0.3)",
                  }}
                >
                  <div className="flex items-center justify-between">
                    <p className="font-mono font-bold" style={{ color: "#f97316", fontSize: 10, letterSpacing: "2px" }}>
                      🎯 SIMULAR LIQUIDACIÓN
                    </p>
                    <button
                      type="button"
                      onClick={closeSimulator}
                      aria-label="Cerrar simulador"
                      data-testid="liqmap-sim-close"
                      className="inline-flex h-6 w-6 items-center justify-center rounded text-white/55 hover:bg-white/10 hover:text-white"
                    >
                      <XIcon size={13} />
                    </button>
                  </div>

                  {/* Entry price */}
                  <label className="flex flex-col gap-1">
                    <span className="font-mono text-[9px] uppercase tracking-wider text-white/55">Precio de entrada</span>
                    <div className="relative flex items-center">
                      <input
                        type="number"
                        inputMode="decimal"
                        step="any"
                        value={simEntryInput}
                        onChange={(e) => setSimEntryInput(e.target.value)}
                        placeholder={refPrice ? String(Number(refPrice.toFixed(2))) : "ej: 67000"}
                        data-testid="liqmap-sim-entry"
                        className="w-full rounded-md border border-white/10 bg-white/[0.04] px-2.5 py-1.5 pr-6 font-mono text-[12px] text-white outline-none focus:border-[#f97316]/60"
                      />
                      <span className="absolute right-2 font-mono text-[10px] text-white/40">$</span>
                    </div>
                  </label>

                  {/* Leverage */}
                  <div className="flex flex-col gap-1.5">
                    <span className="font-mono text-[9px] uppercase tracking-wider text-white/55">
                      Apalancamiento · <span className="text-[#f97316]" data-testid="liqmap-sim-lev-val">{simLeverage}x</span>
                    </span>
                    <input
                      type="range"
                      min={2}
                      max={125}
                      step={1}
                      value={simLeverage}
                      onChange={(e) => setSimLeverage(Number(e.target.value))}
                      data-testid="liqmap-sim-lev-slider"
                      className="w-full accent-[#f97316]"
                    />
                    <div className="flex flex-wrap gap-1">
                      {presets.map((v) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => setSimLeverage(v)}
                          data-testid={`liqmap-sim-lev-${v}`}
                          className={
                            "rounded border px-1.5 py-[1px] font-mono text-[9px] font-bold transition-all " +
                            (simLeverage === v
                              ? "border-[#f97316] bg-[#f97316]/15 text-[#f97316]"
                              : "border-white/10 bg-white/[0.03] text-white/55 hover:border-[#f97316]/40")
                          }
                        >
                          {v}x
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Direction */}
                  <div className="flex flex-col gap-1">
                    <span className="font-mono text-[9px] uppercase tracking-wider text-white/55">Posición</span>
                    <div className="grid grid-cols-2 gap-1.5">
                      <button
                        type="button"
                        onClick={() => setSimDirection("LONG")}
                        data-testid="liqmap-sim-long"
                        className="flex items-center justify-center gap-1 rounded-md py-1.5 font-mono text-[11px] font-bold transition-all"
                        style={{ background: longBg, color: longFg, border: "1px solid rgba(255,255,255,0.06)" }}
                      >
                        <TrendingUp size={12} /> LONG
                      </button>
                      <button
                        type="button"
                        onClick={() => setSimDirection("SHORT")}
                        data-testid="liqmap-sim-short"
                        className="flex items-center justify-center gap-1 rounded-md py-1.5 font-mono text-[11px] font-bold transition-all"
                        style={{ background: shortBg, color: shortFg, border: "1px solid rgba(255,255,255,0.06)" }}
                      >
                        <TrendingDown size={12} /> SHORT
                      </button>
                    </div>
                  </div>

                  {/* Size (optional) */}
                  <label className="flex flex-col gap-1">
                    <span className="font-mono text-[9px] uppercase tracking-wider text-white/55">Tamaño posición (opcional)</span>
                    <div className="relative flex items-center">
                      <input
                        type="number"
                        inputMode="decimal"
                        step="any"
                        value={simSizeInput}
                        onChange={(e) => setSimSizeInput(e.target.value)}
                        placeholder="ej: 1000"
                        data-testid="liqmap-sim-size"
                        className="w-full rounded-md border border-white/10 bg-white/[0.04] px-2.5 py-1.5 pr-6 font-mono text-[12px] text-white outline-none focus:border-[#f97316]/60"
                      />
                      <span className="absolute right-2 font-mono text-[10px] text-white/40">$</span>
                    </div>
                  </label>

                  {/* Calculate */}
                  <button
                    type="button"
                    onClick={handleSimulate}
                    data-testid="liqmap-sim-calc"
                    className="mt-1 w-full rounded-lg py-2 font-mono text-[11px] font-bold uppercase tracking-wider text-black transition-all hover:opacity-90"
                    style={{ background: "linear-gradient(135deg, #f97316, #ea580c)" }}
                  >
                    Calcular liquidación →
                  </button>

                  {/* Share row — copy link + tweet on X */}
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={handleShare}
                      data-testid="liqmap-sim-share"
                      title="Copiar enlace de la simulación"
                      className="flex items-center justify-center gap-1.5 rounded-md border border-[#f97316]/40 bg-[#f97316]/[0.06] py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider text-[#f97316] transition-all hover:bg-[#f97316]/15"
                    >
                      {shareCopied ? <Check size={12} /> : <Share2 size={12} />}
                      {shareCopied ? "Copiado" : "Copiar enlace"}
                    </button>
                    <button
                      type="button"
                      onClick={handleShareTwitter}
                      data-testid="liqmap-sim-tweet"
                      title="Tuitear esta simulación en X"
                      className="flex items-center justify-center gap-1.5 rounded-md border border-white/20 bg-black py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider text-white transition-all hover:bg-white hover:text-black"
                    >
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 1200 1227"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path d="M714 519 1160 0h-105L668 451 359 0H0l468 681L0 1227h105l409-475 327 475h359L714 519zM567 690l-47-68L143 79h161l304 435 47 68 396 567H890L567 690z" />
                      </svg>
                      Tuitear en X
                    </button>
                  </div>

                  {/* Result */}
                  {simResult && (
                    <div className="mt-1 flex flex-col gap-1.5" data-testid="liqmap-sim-result">
                      <p className="font-mono font-black" style={{ color: "#ff3b5c", fontSize: 18 }}>
                        Tu liquidación: ${simResult.liq.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </p>
                      <p className="font-mono text-[10px]" style={{ color: "#f97316" }}>
                        Distancia al precio actual: ${simResult.dist.toLocaleString(undefined, { maximumFractionDigits: 0 })} ({simResult.distPct.toFixed(2)}%)
                      </p>
                      {simResult.size > 0 && (
                        <p className="font-mono text-[10px]" style={{ color: "#ff3b5c" }}>
                          Pérdida estimada: ${simResult.loss.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </p>
                      )}
                      <div
                        data-testid="liqmap-sim-zone"
                        className="mt-1 rounded-md border p-2 font-mono text-[10px] leading-snug"
                        style={
                          simResult.danger
                            ? { background: "rgba(255,59,92,0.10)", borderColor: "rgba(255,59,92,0.4)", color: "#ff8da0" }
                            : { background: "rgba(0,208,132,0.08)", borderColor: "rgba(0,208,132,0.35)", color: "#7adfb5" }
                        }
                      >
                        {simResult.danger
                          ? "🔴 PELIGRO — Tu liquidación cae en zona de alta concentración. Alta probabilidad de cascada."
                          : "🟢 SEGURO — Tu liquidación está en zona de baja concentración de liquidaciones."}
                      </div>
                      <p className="italic" style={{ color: "#444", fontSize: 9 }}>
                        💡 Tip RE₿EL: Evita colocar tu entrada cerca de zonas rojas del heatmap — son imanes de precio donde las liquidaciones en cascada ocurren más.
                      </p>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ─── Cascade Predictor arrow overlay (HTML, not canvas) ─── */}
            {cascadeScore > 55 && cluster.maxPrice != null && (() => {
              const apuntaAbajo = (statsForBar.price || 0) > cluster.maxPrice;
              const color = cascadeScore >= 76 ? "#ff3b5c" : "#ff5a00";
              return (
                <div
                  data-testid="cascade-arrow-overlay"
                  className="pointer-events-none absolute right-16 top-3 z-20 flex items-center gap-1.5 rounded-md border bg-[#080508]/85 px-2 py-1 font-mono text-[9px] font-bold backdrop-blur-sm"
                  style={{
                    color,
                    borderColor: color + "66",
                    animation: cascadeScore >= 76 ? "rebelCascadePulseFast 0.8s infinite" : "none",
                  }}
                >
                  {apuntaAbajo ? <ArrowDown size={11} /> : <ArrowUp size={11} />}
                  <span>⚡ CASCADE ${cluster.maxPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                </div>
              );
            })()}

            {/* ─── Cascade history panel (right-side drawer) ─── */}
            {showHistory && (() => {
              let entries = [];
              try { entries = JSON.parse(localStorage.getItem(`rebel_cascade_history_${coin}`) || "[]"); } catch { /* noop */ }
              const fmtDate = (ms) => new Date(ms).toLocaleString([], { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
              return (
                <div
                  data-testid="cascade-history-panel"
                  className="absolute right-0 top-0 z-30 flex h-full w-[280px] max-w-[90%] flex-col overflow-y-auto rounded-l-2xl border-l p-4"
                  style={{
                    background: "rgba(8,5,8,0.96)",
                    borderColor: "rgba(249,115,22,0.2)",
                    backdropFilter: "blur(10px)",
                    WebkitBackdropFilter: "blur(10px)",
                  }}
                >
                  <div className="mb-3 flex items-center justify-between">
                    <p className="font-mono font-bold" style={{ color: "#f97316", fontSize: 10, letterSpacing: "2px" }}>
                      {t.cascade?.history_title || "HISTORIAL CASCADAS"} · {coin}
                    </p>
                    <button
                      type="button"
                      aria-label={t.cascade?.history_close_aria || "Cerrar historial"}
                      onClick={() => setShowHistory(false)}
                      data-testid="cascade-history-close"
                      className="inline-flex h-6 w-6 items-center justify-center rounded text-white/55 hover:bg-white/10 hover:text-white"
                    >
                      <XIcon size={13} />
                    </button>
                  </div>
                  {entries.length === 0 ? (
                    <p className="text-[11px]" style={{ color: "#444" }}>
                      {(t.cascade?.history_empty || "Sin alertas registradas para {coin}").replace("{coin}", coin)}
                    </p>
                  ) : (
                    <>
                      <CascadeScoreHistoryChart entries={entries} />
                      <ul className="space-y-3">
                      {entries.map((e, i) => {
                        const c = e.nivel === "INMINENTE" ? "#ff3b5c" : e.nivel === "ALTO" ? "#ff5a00" : "#f0c800";
                        return (
                          <li key={`${e.timestamp}-${i}`} className="rounded border border-white/[0.06] p-2">
                            <div className="flex items-center justify-between font-mono text-[9px] uppercase tracking-wider text-white/45">
                              <span>{fmtDate(e.timestamp)}</span>
                              <span style={{ color: c }}>{e.nivel}</span>
                            </div>
                            <div className="mt-1 font-mono text-[10px] text-white/85">
                              Score Predictor: <span style={{ color: c, fontWeight: 700 }}>{e.score}</span>
                              <span className="mx-1.5 opacity-40">·</span>
                              {e.tipo}
                            </div>
                            <div className="mt-0.5 font-mono text-[10px] text-white/55">
                              ${e.precio?.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                              {" → "}${e.cluster_max?.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                            </div>
                            {e.analisis ? (
                              <p className="mt-1 italic text-[10px] leading-snug text-white/65">
                                {String(e.analisis).length > 140 ? `${String(e.analisis).slice(0, 140)}…` : e.analisis}
                              </p>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                    </>
                  )}
                </div>
              );
            })()}

            {/* Legend panel — explanatory color guide */}
            {legendOpen && (
              <div
                ref={legendRef}
                data-testid="liqmap-legend-panel"
                className="absolute bottom-12 left-3 z-30 w-[260px] sm:w-[300px] max-w-[90%] rounded-lg border p-3 text-left"
                style={{
                  background: "rgba(8,5,8,0.95)",
                  borderColor: "rgba(249,115,22,0.2)",
                  backdropFilter: "blur(10px)",
                  WebkitBackdropFilter: "blur(10px)",
                }}
              >
                <p
                  className="mb-2.5 font-mono font-bold"
                  style={{ color: "#f97316", fontSize: 9, letterSpacing: "2px" }}
                >
                  {tL.title}
                </p>
                <ul className="space-y-1.5">
                  {tL.colors.map(([txt, [r, g, b]], i) => (
                    <li key={`c${i}`} className="flex items-start gap-2 text-[10.5px] leading-snug text-white/75">
                      <span
                        className="mt-[3px] inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
                        style={{ background: `rgb(${r},${g},${b})` }}
                      />
                      <span>{txt}</span>
                    </li>
                  ))}
                </ul>
                <div className="my-2.5 h-px w-full bg-white/10" />
                <ul className="space-y-1.5">
                  <li className="flex items-start gap-2 text-[10.5px] leading-snug text-white/75">
                    <span
                      className="mt-[1px] inline-block shrink-0 rounded-sm px-1.5 py-[1px] font-mono text-[8px] font-bold text-white"
                      style={{ background: "#ff3b5c" }}
                    >
                      {tL.max[0]}
                    </span>
                    <span>{tL.max[1]}</span>
                  </li>
                  <li className="flex items-start gap-2 text-[10.5px] leading-snug text-white/75">
                    <span
                      className="mt-[1px] inline-block shrink-0 rounded-sm px-1.5 py-[1px] font-mono text-[8px] font-bold text-black"
                      style={{ background: "#f97316" }}
                    >
                      {tL.sup[0]}
                    </span>
                    <span>{tL.sup[1]}</span>
                  </li>
                  <li className="flex items-start gap-2 text-[10.5px] leading-snug text-white/75">
                    <svg width="22" height="10" className="mt-[3px] shrink-0">
                      <line x1="0" y1="5" x2="22" y2="5" stroke="#f97316" strokeWidth="1.5" strokeDasharray="3 2" />
                    </svg>
                    <span>{tL.live}</span>
                  </li>
                </ul>
                {/* Whale layer legend — only when toggle ON. */}
                {showWhales && (
                  <>
                    <div className="my-2.5 h-px w-full bg-white/10" />
                    <ul className="space-y-1.5">
                      <li className="flex items-start gap-2 text-[10.5px] leading-snug text-white/75">
                        <svg width="22" height="10" className="mt-[3px] shrink-0">
                          <line x1="0" y1="5" x2="22" y2="5" stroke="#FFD700" strokeWidth="2.5" strokeDasharray="6 3" />
                        </svg>
                        <span>🐋 <strong className="text-[#FFD700]">Ballena &gt;$1M</strong> — orden grande detectada en orderbook. Nivel de alta importancia.</span>
                      </li>
                      <li className="flex items-start gap-2 text-[10.5px] leading-snug text-white/75">
                        <svg width="22" height="10" className="mt-[3px] shrink-0">
                          <line x1="0" y1="5" x2="22" y2="5" stroke="#C0C0C0" strokeWidth="1.5" strokeDasharray="3 2" />
                        </svg>
                        <span>🐬 <strong className="text-[#C0C0C0]">Mediana $100K–$1M</strong> — acumulación significativa en este nivel de precio.</span>
                      </li>
                    </ul>
                  </>
                )}
                {/* S/R IA layer legend — only when toggle ON. */}
                {showSR && (
                  <>
                    <div className="my-2.5 h-px w-full bg-white/10" />
                    <ul className="space-y-1.5">
                      <li className="flex items-start gap-2 text-[10.5px] leading-snug text-white/75">
                        <svg width="22" height="10" className="mt-[3px] shrink-0">
                          <line x1="0" y1="5" x2="22" y2="5" stroke="#ff3b5c" strokeWidth="2.5" strokeDasharray="8 4" />
                        </svg>
                        <span><strong className="text-[#ff3b5c]">Resistencia</strong> — nivel donde el precio históricamente rebotó hacia abajo.</span>
                      </li>
                      <li className="flex items-start gap-2 text-[10.5px] leading-snug text-white/75">
                        <svg width="22" height="10" className="mt-[3px] shrink-0">
                          <line x1="0" y1="5" x2="22" y2="5" stroke="#00d084" strokeWidth="2.5" strokeDasharray="8 4" />
                        </svg>
                        <span><strong className="text-[#00d084]">Soporte</strong> — nivel donde el precio históricamente rebotó hacia arriba.</span>
                      </li>
                      {/* Composite Strength Score legend (Fase 2 — 2026-02). */}
                      <li className="flex items-start gap-2 text-[10.5px] leading-snug text-white/75" data-testid="sr-strength-legend">
                        <span className="mt-[1px] inline-flex shrink-0 flex-col gap-[2px]">
                          <svg width="22" height="6"><line x1="0" y1="3" x2="22" y2="3" stroke="#00d084" strokeWidth="2.5" strokeOpacity="1" strokeDasharray="8 4" /></svg>
                          <svg width="22" height="5"><line x1="0" y1="2.5" x2="22" y2="2.5" stroke="#4ade80" strokeWidth="1.5" strokeOpacity="0.70" strokeDasharray="8 4" /></svg>
                          <svg width="22" height="4"><line x1="0" y1="2" x2="22" y2="2" stroke="#86efac" strokeWidth="1.0" strokeOpacity="0.40" strokeDasharray="8 4" /></svg>
                        </span>
                        <span>
                          <strong>{hc.level_strength}</strong> — {hc.level_strength_desc}{" "}
                          <strong className="text-white">Fuerte (≥75)</strong> grosor 2.5px,{" "}
                          <strong>Medio (50-74)</strong> grosor 1.5px,{" "}
                          <strong className="text-white/60">Débil (&lt;50)</strong> grosor 1px.
                        </span>
                      </li>
                      <li className="flex items-start gap-2 text-[10.5px] leading-snug text-white/75">
                        <span className="mt-[1px] inline-block shrink-0 text-[12px]">🔒</span>
                        <span><strong>{hc.confirmed_by_liq}</strong> — {hc.confirmed_by_liq_desc}</span>
                      </li>
                      <li className="flex items-start gap-2 text-[10.5px] leading-snug text-white/75">
                        <span className="mt-[1px] inline-block shrink-0 rounded-sm bg-white/10 px-1.5 py-[1px] font-mono text-[8px] font-bold text-white">72%·5t ⬢84</span>
                        <span><strong>% éxito histórico</strong> sobre nº de toques (<code>5t</code>) y <strong>⬢ score de fuerza</strong> compuesto. % se resalta en blanco cuando ≥60%.</span>
                      </li>
                    </ul>
                  </>
                )}
                <div className="my-2.5 h-px w-full bg-white/10" />
                <p className="italic" style={{ color: "#333", fontSize: 8 }}>
                  {tL.footer(sources.length || 0)}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Stochastic Oscillator subpanel — mounted below the heatmap
            card when the toolbar toggle is on. Uses the same displayed
            candles slice as the main chart so the visual x-axis stays
            aligned. Zero effect on chart layout when disabled. */}
        {showStochastic && candles.length > 0 && (
          <div
            className="mt-1"
            data-testid="liqmap-stochastic-wrap"
          >
            <StochasticSubpanel
              candles={candles}
              lang={lang}
              viewStart={brushRange ? brushRange[0] : 0}
              viewEnd={brushRange ? brushRange[1] : candles.length}
            />
          </div>
        )}

        {/* Time-range brush — sits visually attached below the heatmap
            card. Operates on the full `candles` array (already in memory)
            and emits a [startIdx, endIdx] slice via setBrushRange. The
            heatmap's reset button (↺) clears this state too — see
            HeatmapCanvas.resetZoom + onResetView wiring. */}
        {candles.length > 0 && (
          <div
            className="mt-1 overflow-hidden rounded-2xl border bg-[#02000c]"
            style={{ borderColor: "rgba(249,115,22,0.2)" }}
            data-testid="liqmap-brush-wrap"
          >
            <HeatmapRangeBrush
              candles={candles}
              value={brushRange}
              onChange={setBrushRange}
            />
          </div>
        )}

        {/* Sources footer */}
        {sources.length > 0 && (
          <p className="mt-3 text-[10px] text-white/30" data-testid="liqmap-sources">
            Sources: {sources.join(" · ")}
          </p>
        )}
      </main>

      {/* B-Shadow — captured-chart publish modal. Hidden until
          `shadowOpenSignal` bumps; pre-fills image/title/description/
          tags from the captureChart payload. */}
      <PublishAnalysisModal
        openSignal={shadowOpenSignal}
        initialImage={shadowPrefill?.image || null}
        initialTitle={shadowPrefill?.title || ""}
        initialDescription={shadowPrefill?.description || ""}
        initialTicker={shadowPrefill?.ticker || ""}
        initialTimeframe={shadowPrefill?.timeframe || ""}
        initialDirection={shadowPrefill?.direction || ""}
      />

      {/* Trial overlay (renders OUTSIDE the blurred canvas, no close button) */}
      {trialExpired && <TrialOverlay />}

      {/* ─── Setups recientes modal (S/R alert history) ─── */}
      {srRecentOpen && (
        <div
          className="fixed inset-0 z-[80] flex items-end justify-end p-4 sm:items-center sm:justify-center sm:p-6"
          style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
          onClick={() => setSrRecentOpen(false)}
          data-testid="liqmap-sr-recent-modal"
        >
          <div
            className="relative w-full max-w-[520px] overflow-hidden rounded-xl border"
            style={{
              background: "rgba(8,5,8,0.97)",
              borderColor: "rgba(249,115,22,0.35)",
              maxHeight: "82vh",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="flex items-center justify-between border-b px-4 py-3"
              style={{ borderColor: "rgba(249,115,22,0.2)" }}
            >
              <div>
                <p className="font-mono text-[11px] font-bold tracking-[2px]" style={{ color: "#f97316" }}>
                  📜 SETUPS RECIENTES · 7 DÍAS
                </p>
                <p className="mt-0.5 font-mono text-[9px] text-white/45">
                  Alertas detectadas y verificadas automáticamente cada hora
                </p>
                <p
                  className="mt-0.5 font-mono text-[9px] text-white/35"
                  data-testid="sr-recent-coverage-disclaimer"
                >
                  {tLm(lang).cascade?.coverage_disclaimer}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSrRecentOpen(false)}
                aria-label="Cerrar"
                data-testid="liqmap-sr-recent-close"
                className="inline-flex h-7 w-7 items-center justify-center rounded text-white/60 hover:text-white"
              >
                <XIcon size={14} />
              </button>
            </div>
            <div className="overflow-y-auto" style={{ maxHeight: "calc(82vh - 64px)" }}>
              {/* Coin filter chips */}
              <div className="flex flex-wrap gap-1.5 border-b border-white/5 px-4 py-2.5">
                {[null, "BTC", "ETH", "SOL", "BNB", "XRP", "ZEC"].map((c) => {
                  const active = srRecentCoin === c;
                  return (
                    <button
                      key={c || "all"}
                      type="button"
                      onClick={() => setSrRecentCoin(c)}
                      data-testid={`liqmap-sr-recent-coin-${c || "all"}`}
                      className="rounded-md px-2.5 py-1 font-mono text-[9px] font-bold transition-all"
                      style={active
                        ? { background: "rgba(249,115,22,0.18)", border: "1px solid rgba(249,115,22,0.6)", color: "#f97316" }
                        : { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.55)" }
                      }
                    >
                      {c || "TODAS"}
                    </button>
                  );
                })}
              </div>
              {srRecentLoading && (
                <div className="flex items-center justify-center gap-2 py-10 font-mono text-[11px] text-white/55">
                  <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
                </div>
              )}
              {!srRecentLoading && srRecentData && (() => {
                const items = srRecentData.items || [];
                const stats = srRecentData.stats || {};
                const tasa = stats.tasa_real_pct;
                const tasaCol = tasa == null ? "#888" : tasa >= 65 ? "#00d084" : tasa >= 45 ? "#f0c800" : "#ff3b5c";
                return (
                  <>
                    {/* Stats summary — 5 cols: total / aciertos / fallos / no_testeados / tasa real */}
                    <div className="grid grid-cols-5 gap-2 border-b border-white/5 px-4 py-3 text-center font-mono">
                      <div>
                        <p className="text-[8px] uppercase tracking-wider text-white/45">Total</p>
                        <p className="text-[14px] font-bold text-white">{stats.total || 0}</p>
                      </div>
                      <div>
                        <p className="text-[8px] uppercase tracking-wider text-white/45">Aciertos</p>
                        <p className="text-[14px] font-bold" style={{ color: "#00d084" }}>{stats.aciertos || 0}</p>
                      </div>
                      <div>
                        <p className="text-[8px] uppercase tracking-wider text-white/45">Fallos</p>
                        <p className="text-[14px] font-bold" style={{ color: "#ff3b5c" }}>{stats.fallos || 0}</p>
                      </div>
                      <div title="Niveles que no llegaron a tocarse en la ventana de 4h — no cuentan para la tasa.">
                        <p className="text-[8px] uppercase tracking-wider text-white/45">No testeados</p>
                        <p className="text-[14px] font-bold text-white/55">{stats.no_testeados || 0}</p>
                      </div>
                      <div title="Solo sobre setups donde el nivel sí fue probado (aciertos / (aciertos + fallos)).">
                        <p className="text-[8px] uppercase tracking-wider text-white/45">Tasa real</p>
                        <p className="text-[14px] font-bold" style={{ color: tasaCol }}>
                          {tasa == null ? "—" : `${tasa}%`}
                        </p>
                      </div>
                    </div>
                    {/* Share-to-X CTA — re-enabled 2026-06-25 after the
                        verifier v2 migration was validated end-to-end
                        (Plan A + backfill iter49). The OG card endpoint
                        (/api/liqmap/og/sr-radar) was kept live throughout
                        the pause, so re-enabling is a frontend-only flip. */}
                    {tasa != null && (stats.aciertos + stats.fallos) >= 1 && (() => {
                      const ogUrl = `${API_BASE}/api/liqmap/og/sr-radar?tasa=${tasa}&aciertos=${stats.aciertos || 0}&total=${(stats.aciertos || 0) + (stats.fallos || 0)}&days=${srRecentData.days || 7}`;
                      const tweetText = `📊 Mi radar S/R RE₿EL acertó ${tasa}% de los setups esta semana (${stats.aciertos}/${(stats.aciertos || 0) + (stats.fallos || 0)}).\n\nSoportes y resistencias técnicos confirmados por liquidaciones reales, validados con IA.`;
                      const xUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(ogUrl)}`;
                      return (
                        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/5 px-4 py-2.5">
                          <p className="font-mono text-[9px] text-white/55">
                            🔥 Compartí tus resultados — incluye OG card auto-generado
                          </p>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                try {
                                  navigator.clipboard.writeText(ogUrl);
                                  toast("🔗 Enlace copiado", { duration: 2500 });
                                } catch { /* noop */ }
                              }}
                              data-testid="liqmap-sr-recent-copy"
                              className="rounded-md border border-white/15 px-2.5 py-1 font-mono text-[9px] font-bold text-white/70 hover:border-white/40 hover:text-white"
                            >
                              📋 Copiar
                            </button>
                            <a
                              href={xUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid="liqmap-sr-recent-share-x"
                              className="rounded-md px-2.5 py-1 font-mono text-[9px] font-bold transition-all"
                              style={{
                                background: "#000",
                                border: "1px solid rgba(249,115,22,0.55)",
                                color: "#f97316",
                              }}
                            >
                              𝕏 {hc.share_on_x}
                            </a>
                          </div>
                        </div>
                      );
                    })()}
                    {/* List — with placeholder rows for coins missing in
                        the period (frontend-only injection, NEVER counted
                        in stats, which come from the backend aggregate). */}
                    {(() => {
                      const ALL = ["BTC", "ETH", "SOL", "BNB", "XRP", "ZEC"];
                      const presentCoins = new Set(items.map((x) => x.coin));
                      const targetCoins = srRecentCoin ? [srRecentCoin] : ALL;
                      const missing = targetCoins.filter((c) => !presentCoins.has(c));
                      const monitoringLabel = tLm(lang).cascade?.monitoring_sr || "Monitoreando — sin setup confirmado aún";
                      const showEmptyState = items.length === 0 && missing.length === 0;
                      if (showEmptyState) {
                        return (
                          <p className="px-4 py-8 text-center font-mono text-[11px] text-white/45">
                            Aún no hay setups registrados.<br />
                            Activa <code className="text-[#f97316]">📐 S/R IA</code> en alguna moneda para empezar.
                          </p>
                        );
                      }
                      return (
                        <ul className="divide-y divide-white/5">
                          {items.map((it) => {
                            const isRes = it.kind === "R";
                            const col = isRes ? "#ff3b5c" : "#00d084";
                            const out = it.outcome;
                            const outChip = out === "acierto"
                              ? { bg: "rgba(0,208,132,0.12)", fg: "#00d084", lbl: "✓ Acierto" }
                              : out === "fallo"
                              ? { bg: "rgba(255,59,92,0.12)", fg: "#ff3b5c", lbl: "✗ Fallo" }
                              : out === "no_testeado"
                              ? { bg: "rgba(255,255,255,0.05)", fg: "#a0a0a0", lbl: "➖ No testeado" }
                              : { bg: "rgba(255,255,255,0.06)", fg: "#aaa", lbl: "⌛ Pendiente" };
                            const dt = it.detected_at ? new Date(it.detected_at) : null;
                            const when = dt ? dt.toLocaleString([], { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";
                            const fmt = (n) => n >= 1000 ? `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : `$${Number(n).toFixed(2)}`;
                            return (
                              <li key={it.id} className="px-4 py-2.5 font-mono">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <div className="flex items-center gap-2">
                                    <span
                                      className="inline-block rounded px-1.5 py-[1px] text-[8px] font-bold text-white"
                                      style={{ background: col }}
                                    >
                                      {isRes ? "R" : "S"} · {it.coin}
                                    </span>
                                    <span className="text-[11px] font-bold text-white">
                                      {fmt(it.precio)}
                                    </span>
                                    <span className="text-[9px] text-white/45">{it.timeframe}</span>
                                  </div>
                                  <span
                                    className="rounded px-1.5 py-[1px] text-[9px] font-bold"
                                    style={{ background: outChip.bg, color: outChip.fg }}
                                  >
                                    {outChip.lbl}
                                  </span>
                                </div>
                                <div className="mt-1 flex flex-wrap items-center gap-2 text-[9px] text-white/50">
                                  <span>{it.tasa_acierto_pct}% éxito · {it.num_toques} toques</span>
                                  {it.delta_real_pct != null && (
                                    <span style={{ color: it.delta_real_pct > 0 ? "#00d084" : "#ff3b5c" }}>
                                      Δ {it.delta_real_pct > 0 ? "+" : ""}{it.delta_real_pct.toFixed(2)}%
                                    </span>
                                  )}
                                  <span className="text-white/30">· {when}</span>
                                </div>
                              </li>
                            );
                          })}
                          {/* Placeholder rows for coins with NO data in window.
                              Visually muted, no outcome chip, no metrics —
                              clearly distinct from real entries. */}
                          {missing.map((c) => (
                            <li
                              key={`placeholder-${c}`}
                              className="px-4 py-2.5 font-mono opacity-60"
                              data-testid={`sr-recent-placeholder-${c}`}
                            >
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                  <span
                                    className="inline-block rounded px-1.5 py-[1px] text-[8px] font-bold text-white/55"
                                    style={{ background: "rgba(255,255,255,0.08)" }}
                                  >
                                    {c}
                                  </span>
                                  <span className="text-[10px] italic text-white/45">
                                    {monitoringLabel}
                                  </span>
                                </div>
                              </div>
                            </li>
                          ))}
                        </ul>
                      );
                    })()}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
      {/* Cascade Predictor — Predicciones Recientes drawer (Plan A v2).
          When opened from the in-banner "Historial" button, the panel
          pre-filters to the current coin (Issue 4 fix, 2026-06-25). */}
      <CascadeRecentDrawer
        open={cascadeRecentOpen}
        onClose={() => setCascadeRecentOpen(false)}
        token={token}
        apiBase={API}
        initialCoin={coin}
        lang={lang}
      />
      {/* Cascade Timer histórico (P1). */}
      <CascadeTimerHistoryDrawer
        open={cascadeTimerHistoryOpen}
        onClose={() => setCascadeTimerHistoryOpen(false)}
        token={token}
        apiBase={API}
      />
      {/* Cross-coin Top S/R Levels (Fase 2, 2026-02). */}
      <SrTopLevelsDrawer
        open={srTopLevelsOpen}
        onClose={() => setSrTopLevelsOpen(false)}
        token={token}
        lang={lang}
      />
      {/* Market Structure Analysis (Wyckoff V1) — lateral drawer. */}
      <StructureDrawer
        open={structureOpen}
        onClose={() => setStructureOpen(false)}
        coin={coin}
        tf={timeframe && ["15m","1h","4h","1d"].includes(timeframe) ? timeframe : "1h"}
        token={token}
        apiBase={API_BASE}
        onShareToShadow={(prefill) => {
          setShadowPrefill(prefill);
          setShadowOpenSignal((v) => v + 1);
        }}
      />
    </div>
  );
}
